import React, { useState, useEffect, useCallback, useRef } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import ContentFigure from './ContentFigure';
import PassagePanel from '../PassagePanel';
import TranslateAmharicButton from './TranslateAmharicButton';
import EthiopianFlag from '../EthiopianFlag';
import AIHelpPanel from './AIHelpPanel';
import ComponentTriggers from './ComponentTriggers';
import { authFetch } from '../../services/authFetch';
import { track } from '../../services/api';
import MediaDisplay, { MediaItem } from './MediaDisplay';
// Toast disabled for now - can re-enable later
// import { useToast, ToastContainer } from '../ui/Toast';
import { ToastContainer } from '../ui/Toast';
import { useSession } from '../../contexts/SessionContext';
// CoachCard hidden for launch
// import CoachCard, { CoachAction } from '../CoachCard/CoachCard';
import { Reportable } from '../Reportable/Reportable';
import './MicroLearningModule.css';

interface ModuleProgress {
  status: 'not_started' | 'in_progress' | 'completed';
  completion_percentage: number;
  last_accessed_at?: string;
  score: number;
  max_score: number;
}

interface Module {
  module_id: string;
  title: string;
  description: string;
  subject: string;
  topic: string;
  subtopic?: string;
  difficulty_level: 'easy' | 'medium' | 'hard';
  estimated_duration_minutes: number;
  total_components: number;
  total_questions: number;
  total_flashcards: number;
  learning_objectives: string[];
  student_progress?: ModuleProgress;
}

// All component types from the module generator
// New paradigm (2024): eslce_intel, key_definitions, practice_mcq, assessment_set
type ModuleComponentType =
  | 'lesson_content'
  | 'quiz_question'
  | 'true_false_question'
  | 'flashcard'
  | 'exam_quick_facts'
  | 'key_definitions'
  | 'core_content'
  | 'exam_trap'
  | 'quick_revision'
  | 'practice_mcq'
  | 'real_esslce_question'
  | 'real_eslce_question'
  | 'true_false'
  | 'mnemonic'
  | 'self_check'
  | 'eslce_intel'      // New paradigm: ESLCE exam intelligence
  | 'assessment_set';  // New paradigm: Balanced question sets

interface FollowUpPrompt {
  label: string;
  prompt: string;
  category: 'clarify' | 'deepen' | 'connect' | 'practice' | 'trap';
  priority: 'high' | 'medium' | 'low';
  context_hint: string;
}

interface ComponentData {
  component_id: string;
  component_type: ModuleComponentType;
  title: string;
  order_index: number;
  content_data: any;
  points_value: number;
  follow_up_prompts?: FollowUpPrompt[];
  high_yield?: boolean;
  grounding_note?: string;
  exam_year?: string;
}

interface ComponentListItem {
  component_id: string;
  component_type: string;
  title: string;
  order_index: number;
  student_response?: {
    response_data: Record<string, string>;
    is_correct: boolean;
    score_earned: number;
  };
}

interface ModuleSession {
  module: {
    module_id: string;
    title: string;
    description: string;
    learning_objectives: string[];
    estimated_duration_minutes: number;
  };
  current_component: ComponentData;
  progress: {
    current_component_index: number;
    total_components: number;
    completion_percentage: number;
    score: number;
    max_possible_score: number;
  };
  previous_component_result?: {
    is_correct?: boolean;
    score_earned: number;
    feedback: string;
  };
  component_list?: ComponentListItem[];
  section_completion?: {
    eslce_intel: boolean;
    key_definitions: boolean;
    practice_mcq: boolean;
    assessment_set: boolean;
    real_eslce_question: boolean;
  };
  review_mode?: boolean;  // True when viewing completed module in review mode
  review_stats?: {
    total_questions: number;
    correct_answers: number;
    accuracy: number;
    total_score: number;
    max_possible_score: number;
    time_spent_seconds: number;
    completed_at?: string;
    // Quiz-specific stats (student-friendly)
    quizzes_available?: number;
    quizzes_completed?: number;
    quiz_total_questions?: number;
    quiz_correct_answers?: number;
    quiz_accuracy?: number;
    quiz_scores?: Array<{
      quiz_title: string;
      correct: number;
      total: number;
      percentage: number;
    }>;
  };
}

interface MicroLearningModuleProps {
  studentId: string;
  subject?: string;
  topic?: string;
  subtopic?: string;
  moduleId?: string;  // If provided, start this module directly
  reviewMode?: boolean;  // If true, show review interface instead of sequential flow
  onModuleComplete?: (summary: any) => void;
  onExit?: () => void;  // Called when user clicks Exit
}

/**
 * The words a package uses for the same two controls.
 *
 * "Explain More" and "ESSLCE Tip" are honest on an ESSLCE card, which reveals
 * a fuller definition and cites a real paper. On a UAT card the hidden half is
 * the derivation or the solution to a problem the student has just been shown,
 * and there is no ESSLCE anywhere near it. Keyed by subject so the 26k students
 * on the other side of this component are not touched.
 */
const VOCAB: Record<string, { more: string; less: string; tip: string }> = {
  UAT: { more: '▶ Show the working', less: '▼ Hide the working', tip: 'Exam Tip:' },
};
const DEFAULT_VOCAB = { more: '▶ Explain More', less: '▼ Show Less', tip: 'Exam Tip:' };

/**
 * Subjects whose modules can legitimately carry ESLCE exam intelligence and a
 * real past-paper question, so a MISSING one is worth showing greyed rather
 * than hidden — the module could have had it.
 *
 * A package (UAT, EAU, whatever comes next) never can: there is no ESLCE
 * intelligence for an entrance exam and no ESLCE paper to draw from. Listing
 * subjects here rather than testing for one package's name is what stops the
 * next package inheriting two dead tabs.
 */
const ESLCE_ONLY_SUBJECTS = new Set([
  'Biology', 'Chemistry', 'Physics', 'Mathematics', 'English',
  'Geography', 'History', 'Economics', 'Civics', 'Aptitude',
]);

const MicroLearningModule: React.FC<MicroLearningModuleProps> = ({
  studentId,
  subject,
  topic,
  subtopic,
  moduleId,
  reviewMode = false,
  onModuleComplete,
  onExit
}) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<ModuleSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When the student reaches the end without finishing required work,
  // we show this prompt instead of auto-jumping. They pick: finish the
  // remaining questions in this module, or leave for another module.
  const [stuckPrompt, setStuckPrompt] = useState<{
    coreUnanswered: number;
    quizNeeded: boolean;
    pastExamNeeded: boolean;
    firstUnfinishedIndex: number;
  } | null>(null);
  const [response, setResponse] = useState<any>({});
  const [timeSpent, setTimeSpent] = useState(0);
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showQuizExplanation, setShowQuizExplanation] = useState(false);
  const [quickFactsExpanded, setQuickFactsExpanded] = useState(false);

  // Subject filter for module list
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState<string | null>(subject || null);

  // New paradigm: Track expanded definitions for "Explain More"
  const [expandedDefinitions, setExpandedDefinitions] = useState<Set<number>>(new Set());
  const vocab = (subject && VOCAB[subject]) || DEFAULT_VOCAB;


  // New paradigm: Track current question in assessment set
  const [assessmentIndex, setAssessmentIndex] = useState(0);
  const [assessmentAnswers, setAssessmentAnswers] = useState<Record<number, string>>({});
  const [assessmentRevealed, setAssessmentRevealed] = useState<Set<number>>(new Set());
  const [isRetakingQuiz, setIsRetakingQuiz] = useState(false);
  const [assessmentExplanationsExpanded, setAssessmentExplanationsExpanded] = useState<Set<number>>(new Set());

  // Initial request for AIHelpPanel - used by "Similar Question" feature
  const [aiHelpInitialRequest, setAiHelpInitialRequest] = useState<{
    question: string;
    source_component?: { type: string; content: Record<string, any> };
  } | undefined>(undefined);

  // Answer change tracking for intelligence gathering
  const [answerHistory, setAnswerHistory] = useState<Array<{answer: string, timestamp: number}>>([]);
  const [firstSelectionTime, setFirstSelectionTime] = useState<number | null>(null);

  // Session statistics for AI context
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);
  const [questionsAttempted, setQuestionsAttempted] = useState(0);
  const [questionsCorrect, setQuestionsCorrect] = useState(0);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | undefined>(undefined);

  // Auto-hide previous result feedback
  const [showPreviousResult, setShowPreviousResult] = useState(true);

  // Toast notifications (disabled for now - can re-enable later)
  // const { toasts, dismissToast, success, encourage, tip } = useToast();
  const toasts: any[] = [];
  const dismissToast = (_id: string) => {};

  // Helper: Check if current component is an Extension question (optional practice)
  const isExtensionQuestion = useCallback(() => {
    if (!currentSession?.current_component) return false;
    const title = currentSession.current_component.title || '';
    return title.toLowerCase().includes('extension');
  }, [currentSession]);

  // Helper: Check if current component is a Core question (required practice)
  const isCoreQuestion = useCallback(() => {
    if (!currentSession?.current_component) return false;
    const title = currentSession.current_component.title || '';
    return title.toLowerCase().includes('(core)') || title.toLowerCase().includes('core');
  }, [currentSession]);

  // Helper: Check if we just finished the last Core question (choice point!)
  const isAtCoreChoicePoint = useCallback(() => {
    if (!currentSession?.component_list) return false;

    const componentList = currentSession.component_list;
    const currentIndex = currentSession.progress.current_component_index;

    // Find all core questions
    const coreIndices = componentList
      .map((c, idx) => ({ ...c, idx }))
      .filter(c => c.title?.toLowerCase().includes('(core)') ||
                   (c.component_type === 'practice_mcq' && c.title?.toLowerCase().includes('core')))
      .map(c => c.idx);

    if (coreIndices.length === 0) return false;

    const lastCoreIndex = Math.max(...coreIndices);

    // We're at choice point if current component is the last Core question
    return currentIndex === lastCoreIndex;
  }, [currentSession]);

  // Helper: Check if there are Extension questions available (for choice point)
  const hasExtensionQuestions = useCallback(() => {
    if (!currentSession?.component_list) return false;
    return currentSession.component_list.some(c =>
      c.title?.toLowerCase().includes('extension')
    );
  }, [currentSession]);

  // Helper: Check if there are more assessment_sets after current one
  const hasMoreAssessments = useCallback(() => {
    if (!currentSession?.component_list) return false;
    const currentIndex = currentSession.progress.current_component_index;
    return currentSession.component_list.some((c, idx) =>
      idx > currentIndex && c.component_type === 'assessment_set'
    );
  }, [currentSession]);

  // Helper: Check if module has ANY assessment_set (quiz)
  const hasAnyAssessment = useCallback(() => {
    if (!currentSession?.component_list) return false;
    return currentSession.component_list.some(c => c.component_type === 'assessment_set');
  }, [currentSession]);

  // Helper: Get quiz numbering info (Quiz X of Y)
  const getQuizNumbering = useCallback(() => {
    if (!currentSession?.component_list) return { current: 0, total: 0, allQuizIndices: [] as number[] };
    const componentList = currentSession.component_list;
    const currentIndex = currentSession.progress.current_component_index;
    const allQuizzes = componentList.filter(c => c.component_type === 'assessment_set');
    const currentQuizIndex = allQuizzes.findIndex(q =>
      componentList.indexOf(q) === currentIndex
    );
    return {
      current: currentQuizIndex + 1,
      total: allQuizzes.length,
      allQuizIndices: allQuizzes.map(q => componentList.indexOf(q))
    };
  }, [currentSession]);

  // Helper: Get performance tier with emoji for quiz scores (like Telegram bot)
  const getPerformanceTier = (percentage: number) => {
    if (percentage >= 80) return { emoji: '🌟', message: 'Excellent!' };
    if (percentage >= 60) return { emoji: '👍', message: 'Good job!' };
    if (percentage >= 40) return { emoji: '💪', message: 'Keep practicing!' };
    return { emoji: '📚', message: 'Review needed' };
  };

  // Helper: Check if current component is optional (after assessment_set OR is extension)
  // Core = required, Extension & post-assessment = optional
  const isOptionalComponent = useCallback(() => {
    if (!currentSession?.component_list) return false;

    const currentIndex = currentSession.progress.current_component_index;
    const componentList = currentSession.component_list;
    const currentType = currentSession.current_component?.component_type;
    const currentTitle = currentSession.current_component?.title || '';

    // Question types that can be optional
    const questionTypes = ['quiz_question', 'practice_mcq', 'real_eslce_question', 'real_esslce_question', 'true_false', 'true_false_question'];

    // If not a question type, it's required (learning content)
    if (!questionTypes.includes(currentType)) return false;

    // Extension questions are optional
    if (currentTitle.toLowerCase().includes('extension')) return true;

    // Find the first assessment_set in the component list
    const firstAssessmentIndex = componentList.findIndex(c => c.component_type === 'assessment_set');

    // If no assessment_set exists, all question components are required
    if (firstAssessmentIndex === -1) return false;

    // If we're past the first assessment_set, this question is optional
    return currentIndex > firstAssessmentIndex;
  }, [currentSession]);

  // Session tracking for context assembly
  const {
    trackModuleStart,
    trackModuleComplete,
    trackComponentView,
    trackComponentExit,
    trackAnswerSubmit,
    trackHelpRequest
  } = useSession();

  // Track previous component for exit events
  const previousComponentRef = useRef<string | null>(null);

  // Timer for tracking time spent
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (currentSession) {
      interval = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentSession]);

  // Track component views for session context
  useEffect(() => {
    if (currentSession?.current_component) {
      const component = currentSession.current_component;

      // Track exit of previous component
      if (previousComponentRef.current && previousComponentRef.current !== component.component_id) {
        trackComponentExit(
          previousComponentRef.current,
          currentSession.current_component.component_type,
          selectedModule || undefined
        );
      }

      // Track view of new component
      trackComponentView(
        component.component_id,
        component.component_type,
        selectedModule || undefined,
        component.order_index
      );

      previousComponentRef.current = component.component_id;
    }

    // Cleanup: track exit when unmounting
    return () => {
      if (previousComponentRef.current && currentSession?.current_component) {
        trackComponentExit(
          previousComponentRef.current,
          currentSession.current_component.component_type,
          selectedModule || undefined
        );
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.current_component?.component_id]);

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/micro-learning/modules?student_id=${studentId}&limit=100`;
      // Use selectedSubjectFilter if set, otherwise use subject prop
      const subjectToUse = selectedSubjectFilter || subject;
      if (subjectToUse) {
        url += `&subject=${encodeURIComponent(subjectToUse)}`;
      }
      if (topic) {
        url += `&topic=${encodeURIComponent(topic)}`;
      }
      if (subtopic) {
        url += `&subtopic=${encodeURIComponent(subtopic)}`;
      }
      const response = await authFetch(url);
      const data = await response.json();
      setModules(data.modules || []);
    } catch (err) {
      setError('Failed to load modules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [studentId, subject, selectedSubjectFilter, topic, subtopic]);

  // Load available modules (only if no moduleId provided)
  useEffect(() => {
    if (!moduleId) {
      loadModules();
    }
  }, [loadModules, moduleId]);

  // Restart quiz only (for review mode)
  const restartQuizOnly = async () => {
    const modId = selectedModule || moduleId;
    if (!modId) return;

    try {
      setLoading(true);
      const response = await authFetch(
        `/api/micro-learning/modules/${modId}/restart-quiz?student_id=${studentId}`,
        { method: 'POST' }
      );
      const result = await response.json();
      if (result.success) {
        // Exit review mode and restart
        startModule(modId, true);
      } else {
        setError('Failed to restart quiz');
      }
    } catch (err) {
      setError('Failed to restart quiz');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-start module if moduleId is provided
  useEffect(() => {
    if (moduleId && !currentSession && !loading) {
      // Pass reviewMode to startModule to load session with review=true
      startModule(moduleId, false, reviewMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, reviewMode]);

  // Auto-hide previous result feedback after 3 seconds
  useEffect(() => {
    if (currentSession?.previous_component_result) {
      setShowPreviousResult(true);
      const timer = setTimeout(() => {
        setShowPreviousResult(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentSession?.previous_component_result]);

  const startModule = async (modId: string, restart: boolean = false, review: boolean = false) => {
    try {
      setLoading(true);
      setTimeSpent(0);
      let url = `/api/micro-learning/modules/${modId}/session?student_id=${studentId}`;
      if (restart) {
        url += `&restart=true`;
        // Reset session statistics on restart
        setConsecutiveWrong(0);
        setQuestionsAttempted(0);
        setQuestionsCorrect(0);
        setLastAnswerCorrect(undefined);
      }
      if (review) {
        url += `&review=true`;
      }
      const response = await authFetch(url);
      const session = await response.json();
      setCurrentSession(session);
      setSelectedModule(modId);
      setResponse({});
      setFlashcardFlipped(false);
      setQuizAnswered(false);
      setSelectedAnswer(null);
      setShowQuizExplanation(false);
      setAnswerHistory([]);
      setFirstSelectionTime(null);
      // Reset new paradigm states
      setExpandedDefinitions(new Set());
      setAssessmentIndex(0);
      setAssessmentAnswers({});
      setAssessmentRevealed(new Set());

      // Track module start for session context (not for review)
      if (!review) {
        trackModuleStart(modId);
      }
    } catch (err: any) {
      console.error('Failed to start module:', err);

      // Handle specific error messages from backend
      if (err.response?.status === 402) {
        // A locked lesson, not a broken one. Without this the wall reads as a
        // failure: the student taps a lesson, the page says "Failed to start
        // module. Please try again.", and trying again fails the same way.
        const detail = err.response.data?.detail;
        setError(
          (typeof detail === 'object' && detail?.message) ||
            'This lesson is part of the full package. One lesson in every section is free.'
        );
      } else if (err.response?.status === 404) {
        const errorMessage = err.response.data?.detail || 'Module not found';
        setError(errorMessage);
      } else {
        setError('Failed to start module. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAIQuestion = async (question: string): Promise<string> => {
    // TODO: Integrate with actual AI API endpoint
    // For now, return a mock response
    await new Promise(resolve => setTimeout(resolve, 1500));

    const currentContent = currentSession?.current_component?.content_data;
    const componentType = currentSession?.current_component?.component_type;

    let contextInfo = '';
    if (componentType === 'lesson_content') {
      contextInfo = `Content: ${currentContent?.title || 'Learning content'}`;
    } else if (componentType === 'quiz_question' || componentType === 'true_false_question') {
      contextInfo = `Question: ${currentContent?.question_text || ''}`;
    } else if (componentType === 'flashcard') {
      contextInfo = `Flashcard: ${currentContent?.front || ''}`;
    }

    return `I'm here to help! ${contextInfo}\n\nYour question: "${question}"\n\nThis is a placeholder AI response. The actual AI integration will provide detailed, context-aware explanations to help you understand the concept better.`;
  };


  const submitResponse = async () => {
    if (!currentSession || !selectedModule) return;

    try {
      setLoading(true);
      const response_data = { ...response };

      // Add read completion for lesson content
      if (currentSession.current_component.component_type === 'lesson_content') {
        response_data.read_completion = true;
      }

      const submitResponse = await authFetch(
        `/api/micro-learning/modules/${selectedModule}/components/${currentSession.current_component.component_id}/response?student_id=${studentId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            response_data,
            time_spent_seconds: timeSpent
          })
        }
      );

      const result = await submitResponse.json();

      // Track answer submission for session context
      const isQuestionType = ['quiz_question', 'practice_mcq', 'real_esslce_question', 'true_false_question', 'true_false'].includes(
        currentSession.current_component.component_type
      );
      if (isQuestionType) {
        trackAnswerSubmit(
          currentSession.current_component.component_id,
          result.is_correct || false,
          {
            selected_answer: response?.selected_answer || selectedAnswer,
            time_to_answer_ms: timeSpent * 1000,
            component_type: currentSession.current_component.component_type
          }
        );

        // Update session statistics for AI context
        setQuestionsAttempted(prev => prev + 1);
        setLastAnswerCorrect(result.is_correct || false);

        if (result.is_correct) {
          setQuestionsCorrect(prev => prev + 1);
          setConsecutiveWrong(0); // Reset consecutive wrong on correct answer
        } else {
          setConsecutiveWrong(prev => prev + 1);
        }
      }

      // Silent progression - no alerts

      if (result.module_completed) {
        // Track module completion
        trackModuleComplete(selectedModule);
        // Module completed - get summary
        const summaryResponse = await authFetch(`/api/micro-learning/modules/${selectedModule}/summary?student_id=${studentId}`);
        const summary = await summaryResponse.json();

        if (onModuleComplete) {
          // Embedded mode - let parent handle navigation
          onModuleComplete(summary);
          // Don't show module list - parent will unmount this component
          return;
        }

        // Standalone mode - return to module list
        setCurrentSession(null);
        setSelectedModule(null);
        loadModules(); // Refresh to show updated progress
      } else if (result.next_component) {
        // Continue to next component
        startModule(selectedModule);
      } else {
        // At the end of the module but the gate didn't fire — student
        // skipped required work earlier. Mirror the backend gate rules
        // exactly so the count we show matches what's truly needed:
        //   - ALL core practice MCQs (each counted individually)
        //   - at least 1 assessment_set (counts as 1 if none done)
        //   - at least 1 past-exam (counts as 1 if none done)
        // Extension MCQs are optional (per isOptionalComponent) and
        // never gated.
        const componentList = currentSession.component_list || [];
        const isCorePractice = (c: any) =>
          c.component_type === 'practice_mcq' &&
          c.title &&
          !c.title.toLowerCase().includes('extension');
        const isAssessment = (c: any) => c.component_type === 'assessment_set';
        const isPastExam = (c: any) => c.component_type === 'real_eslce_question';

        const coreUnanswered = componentList.filter(
          (c: any) => isCorePractice(c) && !c.student_response,
        );
        const assessments = componentList.filter(isAssessment);
        const pastExams = componentList.filter(isPastExam);
        const anyAssessmentDone = assessments.some((c: any) => c.student_response);
        const anyPastExamDone = pastExams.some((c: any) => c.student_response);

        // True remaining = core unanswered + (1 if no quiz done) + (1 if no past-exam done)
        const remaining =
          coreUnanswered.length +
          (assessments.length > 0 && !anyAssessmentDone ? 1 : 0) +
          (pastExams.length > 0 && !anyPastExamDone ? 1 : 0);

        // First unanswered required (for the Finish-remaining button):
        // prefer core practice first; then a quiz if none done; then a
        // past-exam if none done.
        let firstUnfinished: any = coreUnanswered[0];
        if (!firstUnfinished && assessments.length > 0 && !anyAssessmentDone) {
          firstUnfinished = assessments.find((c: any) => !c.student_response);
        }
        if (!firstUnfinished && pastExams.length > 0 && !anyPastExamDone) {
          firstUnfinished = pastExams.find((c: any) => !c.student_response);
        }

        if (remaining > 0 && firstUnfinished) {
          setStuckPrompt({
            coreUnanswered: coreUnanswered.length,
            quizNeeded: assessments.length > 0 && !anyAssessmentDone,
            pastExamNeeded: pastExams.length > 0 && !anyPastExamDone,
            firstUnfinishedIndex: firstUnfinished.order_index,
          });
        } else if (onExit) {
          // Defensive: everything required is done but the gate still
          // didn't fire (shouldn't happen). Hand off to parent.
          onExit();
        } else {
          setCurrentSession(null);
          setSelectedModule(null);
          loadModules();
        }
      }

      setResponse({});
      setTimeSpent(0);
    } catch (err) {
      setError('Failed to submit response');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderComponent = () => {
    if (!currentSession) return null;

    const { current_component } = currentSession;
    const { content_data } = current_component;

    switch (current_component.component_type) {
      case 'lesson_content':
        return (
          <div className="lesson-content">
            <h2>{current_component.title}</h2>

            <div className="content-markdown">
              <MarkdownRenderer>{content_data.content_markdown || content_data.content || ''}</MarkdownRenderer>
              <TranslateAmharicButton
                surface="lesson_section"
                cacheKey={current_component.component_id}
                text={content_data.content_markdown || content_data.content || ''}
                trackProps={{
                  component_id: current_component.component_id,
                  module_id: selectedModule || undefined,
                  subject,
                }}
              />
            </div>

            {content_data.key_concepts && (
              <div className="key-concepts">
                <h3>Key Concepts</h3>
                {content_data.key_concepts.map((concept: any, index: number) => (
                  <div key={index} className="concept">
                    <strong><MarkdownRenderer inline>{concept.term}</MarkdownRenderer>:</strong> <MarkdownRenderer inline>{concept.definition}</MarkdownRenderer>
                  </div>
                ))}
              </div>
            )}

            {content_data.summary_points && (
              <div className="summary-points">
                <h3>Summary</h3>
                <ul>
                  {content_data.summary_points.map((point: string, index: number) => (
                    <li key={index}><MarkdownRenderer inline>{point}</MarkdownRenderer></li>
                  ))}
                </ul>
              </div>
            )}

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Continue
            </button>
          </div>
        );

      case 'quiz_question': {
        // Normalize options to array format (handle both array and object)
        let quizOptions: any[] = [];
        const rawQuizOptions = content_data.options || content_data.choices;

        if (Array.isArray(rawQuizOptions)) {
          quizOptions = rawQuizOptions;
        } else if (rawQuizOptions && typeof rawQuizOptions === 'object') {
          // Convert object format to array format
          quizOptions = Object.entries(rawQuizOptions).map(([key, value]) => ({
            label: key,
            text: value,
            is_correct: key === content_data.correct_answer
          }));
        }

        // Handle answer selection (doesn't reveal answer yet)
        const handleQuizSelect = (selectedOption: string) => {
          if (quizAnswered) return; // Can't change after checking

          const now = Date.now();

          // Track first selection time
          if (!firstSelectionTime) {
            setFirstSelectionTime(now);
          }

          // Track answer change if different from current
          if (selectedAnswer && selectedAnswer !== selectedOption) {
            setAnswerHistory(prev => [...prev, { answer: selectedAnswer, timestamp: now }]);
          }

          setSelectedAnswer(selectedOption);
          setResponse({ selected_option: selectedOption });
        };

        // Check/reveal answer
        const handleCheckAnswer = () => {
          if (!selectedAnswer) return;

          // Include answer history in response for intelligence
          setResponse((prev: any) => ({
            ...prev,
            answer_changes: answerHistory.length,
            time_to_first_selection_ms: firstSelectionTime ? firstSelectionTime - (timeSpent * 1000) : null,
            final_answer: selectedAnswer
          }));
          setQuizAnswered(true);
        };

        return (
          <div className="quiz-question">
            {/* Compact header row */}
            <div className="quiz-header-row">
              <span className="quiz-type-badge">❓ Quiz</span>
              {content_data.difficulty_level && (
                <span className={`quiz-difficulty-badge ${content_data.difficulty_level}`}>
                  {content_data.difficulty_level === 'easy' && '🟢 Easy'}
                  {content_data.difficulty_level === 'medium' && '🟡 Medium'}
                  {content_data.difficulty_level === 'hard' && '🔴 Hard'}
                </span>
              )}
            </div>

            {/* The stimulus, ABOVE the question it belongs to.
                A reading-comprehension question is unanswerable without its
                passage, and a deduction question without its statements. They
                ride in enrichment beside the figure a maths item uses. This is
                the one display difference the verbal and analytical topics
                need — everything else about them renders as any other item. */}
            <PassagePanel
              text={content_data.passage}
              surface="quiz_question"
              cacheKey={`${current_component.component_id}:${content_data.question_id || 'q'}`}
              trackProps={{
                component_id: current_component.component_id,
                question_id: content_data.question_id,
                module_id: selectedModule || undefined,
                subject,
              }}
            />

            {/* Question text directly - no redundant title */}
            <div className="question-text"><MarkdownRenderer inline>{content_data.question_text || content_data.stem || ''}</MarkdownRenderer></div>
            {/* The STEM is translatable, not just the explanation. Translate
                used to appear only on the result card, which a student reaches
                by answering — so the one who could not read the question had
                to guess before they could ask for it in Amharic. The cache key
                carries the question id because a quiz component holds five
                questions and a component-level key serves all five the same
                translation. */}
            {(content_data.question_text || content_data.stem) && (
              <TranslateAmharicButton
                surface="quiz_question"
                cacheKey={`${current_component.component_id}:${content_data.question_id || 'q'}:stem`}
                text={content_data.question_text || content_data.stem || ''}
                compact
                trackProps={{
                  component_id: current_component.component_id,
                  component_type: current_component.component_type,
                  question_id: content_data.question_id,
                  module_id: selectedModule || undefined,
                  subject,
                }}
              />
            )}

            <div className="options">
              {quizOptions.map((option: any) => {
                const optionLabel = option.label || option.key;
                const optionText = option.text || option.value;
                const isSelected = selectedAnswer === optionLabel;
                const isCorrect = option.is_correct || optionLabel === content_data.correct_answer;
                let optionClass = "option";

                // Only show correct/incorrect styling AFTER checking answer
                if (quizAnswered && isSelected) {
                  optionClass += isCorrect ? " correct-answer" : " incorrect-answer";
                } else if (quizAnswered && isCorrect) {
                  optionClass += " correct-answer";
                } else if (isSelected) {
                  optionClass += " selected"; // Highlight selection before checking
                }

                return (
                  <label key={optionLabel} className={optionClass}>
                    <input
                      type="radio"
                      name="quiz_option"
                      value={optionLabel}
                      onChange={(e) => handleQuizSelect(e.target.value)}
                      checked={selectedAnswer === optionLabel}
                      disabled={quizAnswered}
                    />
                    <span>{optionLabel}. <MarkdownRenderer inline>{optionText || ''}</MarkdownRenderer></span>
                  </label>
                );
              })}
            </div>

            {/* Answer change indicator (before checking) */}
            {!quizAnswered && answerHistory.length > 0 && (
              <div className="answer-change-hint">
                Changed answer {answerHistory.length} time{answerHistory.length > 1 ? 's' : ''}
              </div>
            )}

            {/* Result card - unified feedback + AI triggers */}
            {quizAnswered && (() => {
              const selectedOpt = quizOptions.find((opt: any) => (opt.label || opt.key) === selectedAnswer);
              const isCorrect = selectedOpt?.is_correct || selectedAnswer === content_data.correct_answer;
              return (
                <div className={`quiz-result-card ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <div className="result-header">
                    <span className="result-icon">{isCorrect ? '🎉' : '💡'}</span>
                    <span className="result-title">{isCorrect ? 'Correct!' : 'Not quite right'}</span>
                  </div>
                  {content_data.explanation && (
                    <div className="result-explanation">
                      <MarkdownRenderer>{content_data.explanation}</MarkdownRenderer>
                      <TranslateAmharicButton
                        surface="quiz_explanation"
                        cacheKey={`${current_component.component_id}:${content_data.question_id || 'q'}:explanation`}
                        text={content_data.explanation}
                        trackProps={{
                          component_id: current_component.component_id,
                          component_type: current_component.component_type,
                          module_id: selectedModule || undefined,
                          subject,
                          is_correct: isCorrect,
                        }}
                      />
                    </div>
                  )}
                  <ComponentTriggers
                    /* The real component type, not a name nothing serves.
                       This asked for "quiz_question" triggers; the configured
                       profiles cover `practice_mcq` and `assessment_set`, so
                       every practice and quiz card fell through to the single
                       hardcoded fallback chip below. */
                    componentType={current_component.component_type}
                    triggerLocation="result_card"
                    subject={subject}
                    context={{ is_correct: isCorrect }}
                    sourceComponentType={current_component.component_type}
                    sourceComponentContent={current_component.content_data}
                    onTriggerClick={(prompt, sourceComponent) => {
                      setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                      setShowAIHelp(true);
                    }}
                    className="result-triggers"
                    fallbackTriggers={[
                      { label: '✨ Similar Question', prompt: 'Generate a similar practice question' }
                    ]}
                  />
                </div>
              );
            })()}

            {/* Two-step flow: Check Answer, then Continue (or options if optional) */}
            {!quizAnswered ? (
              <button
                onClick={handleCheckAnswer}
                className="check-answer-btn"
                disabled={!selectedAnswer || loading}
              >
                Check Answer
              </button>
            ) : isOptionalComponent() ? (
              <div className="quiz-actions">
                <button onClick={submitResponse} className="continue-btn primary" disabled={loading}>
                  Continue →
                </button>
                <button
                  onClick={async () => {
                    try {
                      await authFetch(`/api/micro-learning/modules/${selectedModule}/complete?student_id=${studentId}`, {
                        method: 'POST'
                      });
                    } catch (e) {
                      console.log('Could not mark complete:', e);
                    }
                    setCurrentSession(null);
                    setSelectedModule(null);
                  }}
                  className="finish-btn secondary"
                >
                  ✓ Done
                </button>
              </div>
            ) : (
              <button onClick={submitResponse} className="continue-btn" disabled={loading}>
                Continue
              </button>
            )}
          </div>
        );
      }

      case 'true_false_question':
        return (
          <div className="true-false-question">
            <h2>{current_component.title}</h2>
            <div className="statement"><MarkdownRenderer inline>{content_data.statement}</MarkdownRenderer></div>

            <div className="tf-options">
              <label className="tf-option">
                <input
                  type="radio"
                  name="tf_answer"
                  value="true"
                  onChange={() => setResponse({ selected_answer: true })}
                  checked={response.selected_answer === true}
                />
                <span>True</span>
              </label>
              <label className="tf-option">
                <input
                  type="radio"
                  name="tf_answer"
                  value="false"
                  onChange={() => setResponse({ selected_answer: false })}
                  checked={response.selected_answer === false}
                />
                <span>False</span>
              </label>
            </div>

            <button
              onClick={submitResponse}
              className="submit-btn"
              disabled={response.selected_answer === undefined || loading}
            >
              Submit Answer
            </button>
          </div>
        );

      case 'flashcard':
        return (
          <div className="flashcard">
            <h2>{current_component.title}</h2>

            <div className={`card-container ${flashcardFlipped ? 'flipped' : ''}`}>
              <div className="card" onClick={(e) => { e.preventDefault(); setFlashcardFlipped(!flashcardFlipped); }}>
                <div className="card-face card-front">
                  <div className="card-content">
                    <h3>📝 Question/Term</h3>
                    <p><MarkdownRenderer inline>{content_data.front}</MarkdownRenderer></p>
                    <div className="flip-hint">
                      {!flashcardFlipped && <small>👆 Tap to reveal answer</small>}
                    </div>
                  </div>
                </div>

                <div className="card-face card-back">
                  <div className="card-content">
                    <h3>💡 Answer/Definition</h3>
                    <p><MarkdownRenderer inline>{content_data.back}</MarkdownRenderer></p>
                    {content_data.memory_tip && (
                      <div className="memory-tip">
                        <strong>🧠 Memory Tip:</strong> <MarkdownRenderer inline>{content_data.memory_tip}</MarkdownRenderer>
                      </div>
                    )}
                    <div className="flip-hint">
                      <small>👆 Tap to see question again</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hidden for now - self-assessment feature */}
            {false && flashcardFlipped && (
              <div className="self-assessment">
                <p>How well did you know this? (Optional)</p>
                <div className="assessment-options">
                  <button
                    className={`assessment-btn easy ${response.self_assessment === 'easy' ? 'selected' : ''}`}
                    onClick={() => setResponse({ self_assessment: 'easy' })}
                  >
                    😊 Easy
                  </button>
                  <button
                    className={`assessment-btn medium ${response.self_assessment === 'medium' ? 'selected' : ''}`}
                    onClick={() => setResponse({ self_assessment: 'medium' })}
                  >
                    🤔 Medium
                  </button>
                  <button
                    className={`assessment-btn hard ${response.self_assessment === 'hard' ? 'selected' : ''}`}
                    onClick={() => setResponse({ self_assessment: 'hard' })}
                  >
                    😓 Hard
                  </button>
                </div>
              </div>
            )}

            {/* AI triggers - shown after flip - fetched from API */}
            {flashcardFlipped && (
              <ComponentTriggers
                componentType="flashcard"
                triggerLocation="component_inline"
                subject={subject}
                templateVars={{ front: content_data.front }}
                sourceComponentType="flashcard"
                sourceComponentContent={{ front: content_data.front, back: content_data.back }}
                onTriggerClick={(prompt, sourceComponent) => {
                  setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                  setShowAIHelp(true);
                }}
                className="flashcard-triggers"
                fallbackTriggers={[
                  { label: '🧠 Memory Trick', prompt: `Give me a memory trick or mnemonic to remember: "${content_data.front}"` },
                  { label: '🔀 Confused with?', prompt: `What similar terms or concepts might I confuse with "${content_data.front}"? Help me distinguish them.` },
                  { label: '✨ Similar Card', prompt: 'Generate a similar flashcard on the same topic' }
                ]}
              />
            )}

            <div className="flashcard-actions">
              {!flashcardFlipped ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setFlashcardFlipped(true);
                  }}
                  className="continue-btn"
                >
                  Show Answer
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (!response.self_assessment) {
                      setResponse({ self_assessment: 'medium' });
                    }
                    submitResponse();
                  }}
                  className="continue-btn"
                  disabled={loading}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        );

      // New component types from v2 module generator
      case 'exam_quick_facts':
        return (
          <div className="exam-quick-facts">
            <div className="component-badge high-yield">Quick Facts</div>
            {current_component.high_yield && <span className="high-yield-badge">High Yield</span>}
            <h2>{current_component.title}</h2>

            {/* Collapsible toggle for exam statistics */}
            <button
              className="quick-facts-toggle"
              onClick={() => setQuickFactsExpanded(!quickFactsExpanded)}
            >
              <span className="toggle-label">
                {quickFactsExpanded ? 'Hide Exam Statistics' : 'View Exam Statistics'}
              </span>
              <span className={`toggle-chevron ${quickFactsExpanded ? 'expanded' : ''}`}>
                ▼
              </span>
            </button>

            {/* Collapsible content wrapper */}
            <div className={`quick-facts-content ${quickFactsExpanded ? 'expanded' : ''}`}>
              {/* Questions per exam stat */}
              {content_data.questions_per_exam && (
                <div className="exam-stat">
                  <strong>Avg. Questions per Exam:</strong> ~{content_data.questions_per_exam}
                </div>
              )}

              {/* Difficulty trend */}
              {content_data.difficulty_trend && (
                <div className="difficulty-trend">
                  <strong>Difficulty Trend:</strong> {content_data.difficulty_trend}
                </div>
              )}

              {/* Most tested subtopics */}
              {content_data.most_tested_subtopics && content_data.most_tested_subtopics.length > 0 && (
                <div className="tested-subtopics">
                  <strong>Most Tested:</strong>
                  <ul>
                    {content_data.most_tested_subtopics.map((topic: string, index: number) => (
                      <li key={index}>{topic}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Typical question types */}
              {content_data.typical_question_types && content_data.typical_question_types.length > 0 && (
                <div className="question-types">
                  <strong>Question Types:</strong>
                  <ul>
                    {content_data.typical_question_types.map((qtype: string, index: number) => (
                      <li key={index}>{qtype}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Legacy facts array support */}
              {content_data.facts && content_data.facts.length > 0 && (
                <div className="facts-list">
                  {content_data.facts.map((fact: string, index: number) => (
                    <div key={index} className="fact-item">
                      <span className="fact-text"><MarkdownRenderer inline>{fact}</MarkdownRenderer></span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fallback content */}
              {!content_data.facts && !content_data.most_tested_subtopics && (
                <div className="content-markdown">
                  <MarkdownRenderer>{content_data.content || content_data.content_markdown || ''}</MarkdownRenderer>
                </div>
              )}
            </div>

            {content_data.exam_tip && (
              <div className="exam-tip">
                <strong>💡 Exam Tip:</strong> <MarkdownRenderer inline>{content_data.exam_tip}</MarkdownRenderer>
              </div>
            )}

            {current_component.grounding_note && (
              <div className="grounding-note">
                <span className="grounding-icon">📚</span>
                <MarkdownRenderer inline>{current_component.grounding_note}</MarkdownRenderer>
              </div>
            )}

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Continue
            </button>
          </div>
        );

      case 'key_definitions': {
        // Helper to toggle expanded definitions
        const toggleExpanded = (index: number) => {
          setExpandedDefinitions(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
              next.delete(index);
            } else {
              next.add(index);
            }
            return next;
          });
        };

        return (
          <div className="key-definitions">
            <div className="component-badge definitions">📖 Key Definitions</div>
            <h2>{current_component.title}</h2>

            <div className="definitions-list">
              {content_data.definitions?.map((def: any, index: number) => {
                const isExpanded = expandedDefinitions.has(index);
                const hasBriefDef = def.brief_definition;
                const hasDetailedExpl = def.detailed_explanation;

                return (
                  <div key={index} className={`definition-item priority-${def.memorization_priority || 'medium'} ${isExpanded ? 'expanded' : ''}`}>
                    <div className="term-header">
                      <dt className="term">{def.term}</dt>
                      {def.memorization_priority && (
                        <span className={`priority-badge priority-${def.memorization_priority}`}>
                          {def.memorization_priority === 'high' ? '⭐ Must Know' :
                           def.memorization_priority === 'medium' ? 'Important' : 'Good to Know'}
                        </span>
                      )}
                    </div>

                    {/* Brief definition (new paradigm) or legacy definition.
                        BLOCK, not inline. `MarkdownRenderer inline` forces
                        `.inline-markdown p { display: inline }`, which
                        collapses every paragraph boundary into the running
                        text — so a card written as a claim, then stacked
                        declarations, then an equation, rendered as one
                        run-on paragraph with the declarations glued to the
                        sentence before them. Single newlines survived as
                        <br> (remark-breaks) and blank lines did not, which
                        is what made the bug look like "the content did not
                        change". The card body is a block of prose and
                        mathematics; `.meaning` supplies the spacing. */}
                    <dd className="meaning">
                      <MarkdownRenderer className="card-body">
                        {hasBriefDef ? def.brief_definition : (def.definition || def.meaning)}
                      </MarkdownRenderer>
                    </dd>

                    {/* The figure sits AFTER the description it supports.
                        Above it, a diagram arrives before the sentence that
                        says what it is of, and the reader has to hold it
                        unexplained; below, it confirms something they have
                        just read. Inline SVG in currentColor, so it follows
                        dark mode. Only UAT authors this field — every ESSLCE
                        component in production has no `figure`. */}
                    {def.figure && <ContentFigure figure={def.figure} />}

                    {/* Always-on Amharic translate for the summary itself — the
                        list-level content, available without expanding. Distinct
                        cacheKey (:summary) so it doesn't collide with the detailed
                        explanation's translation below. */}
                    {(def.brief_definition || def.definition || def.meaning) && (
                      <TranslateAmharicButton
                        surface="key_definition"
                        contentType="key_term"
                        cacheKey={`${current_component.component_id}:${def.term}:summary`}
                        text={hasBriefDef ? def.brief_definition : (def.definition || def.meaning)}
                        compact
                        trackProps={{
                          component_id: current_component.component_id,
                          module_id: selectedModule || undefined,
                          subject,
                          term: def.term,
                          part: 'summary',
                        }}
                      />
                    )}

                    {/* Explain More button and detailed explanation (new paradigm) */}
                    {hasDetailedExpl && (
                      <>
                        <button
                          className={`explain-more-btn ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleExpanded(index)}
                        >
                          {isExpanded ? vocab.less : vocab.more}
                        </button>

                        {isExpanded && (
                          <div className="detailed-explanation">
                            <MarkdownRenderer>{def.detailed_explanation}</MarkdownRenderer>
                            <TranslateAmharicButton
                              surface="key_definition"
                              cacheKey={`${current_component.component_id}:${def.term}`}
                              text={def.detailed_explanation}
                              compact
                              trackProps={{
                                component_id: current_component.component_id,
                                module_id: selectedModule || undefined,
                                subject,
                                term: def.term,
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {/* Legacy fields - only show if no detailed_explanation */}
                    {!hasDetailedExpl && (
                      <>
                        {def.example && <div className="example">Example: <MarkdownRenderer inline>{def.example}</MarkdownRenderer></div>}
                        {def.context && <div className="context">Context: <MarkdownRenderer inline>{def.context}</MarkdownRenderer></div>}
                      </>
                    )}

                    {/* Exam tip - show for both paradigms */}
                    {def.exam_tip && (
                      <div className="exam-tip">
                        <span className="tip-icon">💡</span>
                        <strong>{vocab.tip}</strong> <MarkdownRenderer inline>{def.exam_tip}</MarkdownRenderer>
                      </div>
                    )}

                    {def.exam_relevance && (
                      <div className="exam-relevance">
                        <strong>Why it matters:</strong> <MarkdownRenderer inline>{def.exam_relevance}</MarkdownRenderer>
                      </div>
                    )}


                    {/* Per-item AI triggers - fetched from API */}
                    <ComponentTriggers
                      componentType="key_definitions"
                      triggerLocation="definition_item"
                      subject={subject}
                      templateVars={{ term: def.term }}
                      sourceComponentType="key_definition"
                      sourceComponentContent={{ term: def.term, definition: def.brief_definition || def.definition || def.meaning }}
                      onTriggerClick={(prompt, sourceComponent) => {
                        setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                        setShowAIHelp(true);
                      }}
                      className="definition-triggers"
                      fallbackTriggers={[
                        { label: '💬 Explain', prompt: `Explain "${def.term}" in simpler terms.` },
                        { label: '❓ Quiz me', prompt: `Quiz me on "${def.term}" - give me a quick question to test if I understand it.` }
                      ]}
                    />
                  </div>
                );
              }) || (
                <div className="content-markdown">
                  <MarkdownRenderer>{content_data.content || content_data.content_markdown || ''}</MarkdownRenderer>
                </div>
              )}
            </div>

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Continue
            </button>
          </div>
        );
      }

      case 'core_content':
        // Support both key_points and key_takeaways
        const keyPointsList = content_data.key_points || content_data.key_takeaways || [];
        return (
          <div className="core-content">
            <div className="component-badge core">📚 Learn</div>
            {current_component.high_yield && <span className="high-yield-badge">🎯 High Yield</span>}
            <h2>{current_component.title}</h2>

            <div className="content-markdown">
              <MarkdownRenderer>{content_data.content_markdown || content_data.content || ''}</MarkdownRenderer>
            </div>

            {keyPointsList.length > 0 && (
              <div className="key-points">
                <h3>🔑 Key Takeaways</h3>
                <ul>
                  {keyPointsList.map((point: string, index: number) => (
                    <li key={index}><MarkdownRenderer inline>{point}</MarkdownRenderer></li>
                  ))}
                </ul>
              </div>
            )}

            {current_component.grounding_note && (
              <div className="grounding-note">
                <span className="grounding-icon">📚</span>
                <MarkdownRenderer inline>{current_component.grounding_note}</MarkdownRenderer>
              </div>
            )}

            {/* AI triggers for content - fetched from API */}
            <ComponentTriggers
              componentType="core_content"
              triggerLocation="content_footer"
              subject={subject}
              sourceComponentType="core_content"
              sourceComponentContent={{ title: current_component.title, key_points: keyPointsList }}
              onTriggerClick={(prompt, sourceComponent) => {
                setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                setShowAIHelp(true);
              }}
              className="content-triggers"
              size="medium"
              fallbackTriggers={[
                { label: '🔄 Simplify', prompt: `Explain this concept in simpler terms: "${current_component.title}"` },
                { label: '🌍 Examples', prompt: `Give me concrete, everyday examples of "${current_component.title}".` },
                { label: '❓ Quiz me', prompt: `Quiz me on "${current_component.title}" - test my understanding with a question.` }
              ]}
            />

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Continue
            </button>
          </div>
        );

      case 'exam_trap':
        return (
          <div className="exam-trap">
            <div className="component-badge trap">⚠️ Exam Trap</div>
            {content_data.frequency && (
              <span className={`frequency-badge frequency-${content_data.frequency.replace('_', '-')}`}>
                {content_data.frequency === 'very_common' ? 'Very Common' :
                 content_data.frequency === 'common' ? 'Common' : 'Occasional'}
              </span>
            )}
            <h2>{current_component.title}</h2>

            <div className="trap-content">
              <div className="trap-warning">
                <strong>Common Mistake:</strong>
              </div>
              <div className="trap-description">
                <MarkdownRenderer>{content_data.trap_description || content_data.content || ''}</MarkdownRenderer>
                <TranslateAmharicButton
                  surface="eslce_intel"
                  cacheKey={current_component.component_id}
                  text={content_data.trap_description || content_data.content || ''}
                  trackProps={{
                    component_id: current_component.component_id,
                    module_id: selectedModule || undefined,
                    subject,
                  }}
                />
              </div>

              {content_data.why_students_fall && (
                <div className="why-fall">
                  <strong>Why students fall for this:</strong>
                  <p><MarkdownRenderer inline>{content_data.why_students_fall}</MarkdownRenderer></p>
                </div>
              )}

              {(content_data.correct_understanding || content_data.correct_approach) && (
                <div className="correct-understanding">
                  <span className="correct-icon">✅</span>
                  <strong>What you should know:</strong>
                  <p><MarkdownRenderer inline>{content_data.correct_understanding || content_data.correct_approach}</MarkdownRenderer></p>
                </div>
              )}

              {(content_data.example_wrong_answer || content_data.example_right_answer) && (
                <div className="example-answers">
                  {content_data.example_wrong_answer && (
                    <div className="example-wrong">
                      <span className="wrong-icon">❌</span>
                      <strong>Wrong answer trap:</strong>
                      <p><MarkdownRenderer inline>{content_data.example_wrong_answer}</MarkdownRenderer></p>
                    </div>
                  )}
                  {content_data.example_right_answer && (
                    <div className="example-right">
                      <span className="right-icon">✅</span>
                      <strong>Correct answer:</strong>
                      <p><MarkdownRenderer inline>{content_data.example_right_answer}</MarkdownRenderer></p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* AI triggers for exam trap - fetched from API */}
            <ComponentTriggers
              componentType="exam_trap"
              triggerLocation="component_inline"
              subject={subject}
              sourceComponentType="exam_trap"
              sourceComponentContent={{
                title: current_component.title,
                trap_description: content_data.trap_description || content_data.content
              }}
              onTriggerClick={(prompt, sourceComponent) => {
                setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                setShowAIHelp(true);
              }}
              className="trap-triggers"
              size="medium"
              fallbackTriggers={[
                { label: '📝 Real Exam Example', prompt: `Show me a real ESSLCE question where students commonly fall for this trap: "${current_component.title}"` },
                { label: '🛡️ How to Avoid', prompt: `Give me specific strategies to avoid falling for this trap on the ESSLCE: "${current_component.title}"` },
                { label: '🎯 Test Me', prompt: `Test me with a question that uses this trap to see if I'd fall for it: "${current_component.title}"` }
              ]}
            />

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Got it! Continue
            </button>
          </div>
        );

      case 'quick_revision':
        return (
          <div className="quick-revision">
            <div className="component-badge revision">🔄 Quick Revision</div>
            <h2>{current_component.title}</h2>

            <div className="revision-content">
              {content_data.summary && (
                <div className="summary-section">
                  <MarkdownRenderer>{content_data.summary}</MarkdownRenderer>
                </div>
              )}

              {content_data.bullet_points && (
                <ul className="revision-points">
                  {content_data.bullet_points.map((point: string, index: number) => (
                    <li key={index}><MarkdownRenderer inline>{point}</MarkdownRenderer></li>
                  ))}
                </ul>
              )}

              {!content_data.summary && !content_data.bullet_points && (
                <div className="content-markdown">
                  <MarkdownRenderer>{content_data.content || content_data.content_markdown || ''}</MarkdownRenderer>
                </div>
              )}
            </div>

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Continue
            </button>
          </div>
        );

      case 'practice_mcq':
      case 'real_esslce_question':
      case 'real_eslce_question': {
        const isRealExam = current_component.component_type === 'real_esslce_question' ||
                          current_component.component_type === 'real_eslce_question';

        // For real ESLCE questions, enrichment data is in metadata object
        const metadata = content_data.metadata || {};
        const correctAnswer = content_data.correct_answer || metadata.correct_answer;
        const explanation = content_data.explanation || metadata.explanation;
        const topic = content_data.topic_covered || metadata.topic;
        const subtopic = metadata.subtopic;
        const difficultyLevel = content_data.difficulty_level || metadata.difficulty_level;

        // Normalize options to array format
        // Handle both formats:
        // - Array: [{label: "A", text: "...", is_correct: true}]
        // - Object: {"A": "option text", "B": "option text"} with separate correct_answer field
        let normalizedOptions: any[] = [];
        const rawOptions = content_data.options || content_data.choices;

        if (Array.isArray(rawOptions)) {
          // Already in array format
          normalizedOptions = rawOptions;
        } else if (rawOptions && typeof rawOptions === 'object') {
          // Convert object format to array format
          normalizedOptions = Object.entries(rawOptions).map(([key, value]) => ({
            label: key,
            text: value,
            is_correct: key === correctAnswer
          }));
        }

        // Handle answer selection (doesn't reveal answer yet)
        const handleMCQSelect = (selectedOption: string) => {
          if (quizAnswered) return; // Can't change after checking

          const now = Date.now();

          // Track first selection time
          if (!firstSelectionTime) {
            setFirstSelectionTime(now);
          }

          // Track answer change if different from current
          if (selectedAnswer && selectedAnswer !== selectedOption) {
            setAnswerHistory(prev => [...prev, { answer: selectedAnswer, timestamp: now }]);
          }

          setSelectedAnswer(selectedOption);
          setResponse({ selected_option: selectedOption });
        };

        // Check/reveal answer
        const handleCheckMCQAnswer = () => {
          if (!selectedAnswer) return;

          // Include answer history in response for intelligence
          setResponse((prev: any) => ({
            ...prev,
            answer_changes: answerHistory.length,
            time_to_first_selection_ms: firstSelectionTime ? firstSelectionTime - (timeSpent * 1000) : null,
            final_answer: selectedAnswer
          }));
          setQuizAnswered(true);
          track('microlearning.check_answer.clicked', {
            module_id: selectedModule,
            component_id: current_component.component_id,
            component_type: 'practice_mcq',
            topic,
            is_correct: selectedAnswer === correctAnswer,
            answer_changes: answerHistory.length,
            time_spent_s: timeSpent,
          });
        };

        return (
          <div className={`quiz-question ${isRealExam ? 'real-exam' : 'practice'}`}>
            {/* Compact header row - badges inline */}
            <div className="quiz-header-row">
              {isRealExam ? (
                <span className="quiz-type-badge exam">📝 ESSLCE {content_data.exam_year && `'${content_data.exam_year.toString().slice(-2)}`}</span>
              ) : (
                <span className="quiz-type-badge practice">🎯 Practice</span>
              )}
              {difficultyLevel && (
                <span className={`quiz-difficulty-badge ${difficultyLevel.toLowerCase()}`}>
                  {difficultyLevel.toLowerCase() === 'easy' && '🟢 Easy'}
                  {difficultyLevel.toLowerCase() === 'medium' && '🟡 Medium'}
                  {difficultyLevel.toLowerCase() === 'hard' && '🔴 Hard'}
                </span>
              )}
              {topic && (
                <span className="quiz-topic-badge">📚 {topic}{subtopic ? ` → ${subtopic}` : ''}</span>
              )}
            </div>

            {/* Detect questions whose answer choices ARE images (e.g.
                "Which of the following graphs..."). Media tagged with
                relationship_type=option_a/b/c/d are rendered as tappable
                options instead of stimulus images. */}
            {(() => {
              const allMedia = (content_data.media || []) as MediaItem[];
              const optionMedia = allMedia.filter(
                (m: any) => typeof m.relationship_type === 'string' && m.relationship_type.startsWith('option_')
              ).sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
              const stimulusMedia = allMedia.filter(
                (m: any) => !(typeof m.relationship_type === 'string' && m.relationship_type.startsWith('option_'))
              );
              const hasImageOptions = optionMedia.length > 0 && normalizedOptions.length === 0;

              return (
                <>
                  {/* Stimulus media (zoomable; never option-media) */}
                  {stimulusMedia.length > 0 && (
                    <MediaDisplay
                      media={stimulusMedia}
                      showCaption={true}
                      maxHeight={280}
                      allowZoom={true}
                    />
                  )}

                  {/* The stimulus, ABOVE the question it belongs to.
                      A reading-comprehension question without its passage, or
                      a deduction question without its statements, cannot be
                      answered at all — it is not merely plainer. This block
                      existed only under `quiz_question`, a component type the
                      UAT lessons never use, so every verbal passage we import
                      was reaching the client and never being drawn. */}
                  <PassagePanel
                    text={content_data.passage}
                    surface="assessment_set"
                    cacheKey={`${current_component.component_id}:${content_data.question_id || 'q'}`}
                    trackProps={{
                      component_id: current_component.component_id,
                      question_id: content_data.question_id,
                      module_id: selectedModule || undefined,
                      subject,
                    }}
                  />

                  {/* Question text */}
                  <div className="question-text"><MarkdownRenderer inline>{content_data.question_text || content_data.stem || ''}</MarkdownRenderer></div>

                  {/* Authored SVG stimulus — see the note in assessment_set. */}
                  <ContentFigure figure={content_data.figure} />

                  {hasImageOptions ? (
                    <div className="options">
                      {optionMedia.map((m: any) => {
                        const label = m.relationship_type.replace('option_', '').toUpperCase();
                        const isSelected = selectedAnswer === label;
                        const isCorrectOption = label === correctAnswer;
                        let optionClass = "option image-option";
                        if (quizAnswered && isSelected) {
                          optionClass += isCorrectOption ? " correct-answer" : " incorrect-answer";
                        } else if (quizAnswered && isCorrectOption) {
                          optionClass += " correct-answer";
                        } else if (isSelected) {
                          optionClass += " selected";
                        }
                        return (
                          <label key={label} className={optionClass}>
                            <input
                              type="radio"
                              name="quiz_option"
                              value={label}
                              onChange={(e) => handleMCQSelect(e.target.value)}
                              checked={isSelected}
                              disabled={quizAnswered}
                            />
                            <span className="image-option-label">{label}.</span>
                            <img
                              src={m.media_url}
                              alt={`Option ${label}`}
                              className="image-option-img"
                            />
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="options">
                      {normalizedOptions.map((option: any) => {
                        const optionLabel = option.label || option.key;
                        const optionText = option.text || option.value;
                        const isSelected = selectedAnswer === optionLabel;
                        const isCorrectOption = option.is_correct || option.correct || optionLabel === correctAnswer;
                        let optionClass = "option";
                        if (quizAnswered && isSelected) {
                          optionClass += isCorrectOption ? " correct-answer" : " incorrect-answer";
                        } else if (quizAnswered && isCorrectOption) {
                          optionClass += " correct-answer";
                        } else if (isSelected) {
                          optionClass += " selected";
                        }
                        return (
                          <label key={optionLabel} className={optionClass}>
                            <input
                              type="radio"
                              name="quiz_option"
                              value={optionLabel}
                              onChange={(e) => handleMCQSelect(e.target.value)}
                              checked={selectedAnswer === optionLabel}
                              disabled={quizAnswered}
                            />
                            <span>{optionLabel}. <MarkdownRenderer inline>{optionText || ''}</MarkdownRenderer></span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Answer change indicator (before checking) */}
            {!quizAnswered && answerHistory.length > 0 && (
              <div className="answer-change-hint">
                Changed answer {answerHistory.length} time{answerHistory.length > 1 ? 's' : ''}
              </div>
            )}

            {/* Result card - unified feedback + AI triggers */}
            {quizAnswered && (() => {
              const selectedOption = normalizedOptions.find(
                (opt: any) => (opt.label || opt.key) === selectedAnswer
              );
              const isAnswerCorrect = selectedOption?.is_correct || selectedOption?.correct || selectedAnswer === correctAnswer;
              const correctOptionText = normalizedOptions.find(
                (opt: any) => (opt.label || opt.key) === correctAnswer
              )?.text || '';
              return (
                <div className="quiz-result-card">
                  <div className="result-header">
                    <span className={`result-badge ${isAnswerCorrect ? 'correct' : 'incorrect'}`}>
                      {isAnswerCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  </div>
                  {/* Show correct answer for wrong answers */}
                  {!isAnswerCorrect && correctAnswer && (
                    <div className="correct-answer-info">
                      <strong>Correct Answer:</strong> {correctAnswer}. <MarkdownRenderer inline>{correctOptionText}</MarkdownRenderer>
                    </div>
                  )}
                  {explanation && (
                    <button
                      className="explanation-toggle"
                      onClick={() => setShowQuizExplanation(!showQuizExplanation)}
                    >
                      {showQuizExplanation ? '▼ Hide Explanation' : '▶ Show Explanation'}
                    </button>
                  )}
                  {explanation && showQuizExplanation && (
                    <div className="result-explanation"><MarkdownRenderer>{explanation}</MarkdownRenderer></div>
                  )}
                  {showQuizExplanation && topic && (
                    <span className="result-topic-badge">📍 {topic}{subtopic ? ` → ${subtopic}` : ''}</span>
                  )}
                  <ComponentTriggers
                    componentType="practice_mcq"
                    triggerLocation="result_card"
                    subject={subject}
                    context={{ is_correct: isAnswerCorrect }}
                    sourceComponentType={current_component.component_type}
                    sourceComponentContent={current_component.content_data}
                    onTriggerClick={(prompt, sourceComponent) => {
                      setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                      setShowAIHelp(true);
                    }}
                    className="result-triggers"
                    fallbackTriggers={[
                      { label: '✨ Similar Question', prompt: 'Generate a similar practice question' }
                    ]}
                  />
                </div>
              );
            })()}

            {current_component.grounding_note && (
              <div className="grounding-note">
                <span className="grounding-icon">📚</span>
                <MarkdownRenderer inline>{current_component.grounding_note}</MarkdownRenderer>
              </div>
            )}

            {/* Two-step flow: Check Answer, then Continue (or options) */}
            {!quizAnswered ? (
              <button
                onClick={handleCheckMCQAnswer}
                className="check-answer-btn"
                disabled={!selectedAnswer || loading}
              >
                Check Answer
              </button>
            ) : isAtCoreChoicePoint() && hasExtensionQuestions() && hasAnyAssessment() ? (
              /* After Core: Continue to Quiz, or More Practice for geeks (only if quiz exists) */
              <div className="quiz-actions">
                <button
                  onClick={async () => {
                    // First save the current question response, then skip to assessment
                    try {
                      setLoading(true);

                      // Save current practice question response before skipping
                      const saveResponse = await authFetch(
                        `/api/micro-learning/modules/${selectedModule}/components/${current_component.component_id}/response?student_id=${studentId}`,
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            response_data: response,
                            time_spent_seconds: timeSpent
                          })
                        }
                      );

                      if (!saveResponse.ok) {
                        console.log('Failed to save response, continuing anyway');
                      }

                      // Now skip to assessment
                      const skipResponse = await authFetch(
                        `/api/micro-learning/modules/${selectedModule}/skip-to-assessment?student_id=${studentId}`,
                        { method: 'POST' }
                      );
                      const result = await skipResponse.json();

                      // Check if skip was successful before reloading
                      if (skipResponse.ok && result.success && selectedModule) {
                        startModule(selectedModule);
                      } else {
                        // Fallback to normal continue if skip failed
                        console.log('Skip to assessment failed, continuing normally');
                        submitResponse();
                      }
                    } catch (err) {
                      console.error('Failed to skip to assessment:', err);
                      // Fallback to normal continue
                      submitResponse();
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="continue-btn primary"
                  disabled={loading}
                >
                  Continue →
                </button>
                <button onClick={submitResponse} className="more-practice-btn secondary" disabled={loading}>
                  💪 More Practice
                </button>
              </div>
            ) : (
              <button onClick={submitResponse} className="continue-btn" disabled={loading}>
                Continue
              </button>
            )}
          </div>
        );
      }

      case 'true_false': {
        // Alias for true_false_question with enhanced UI
        const correctAnswer = content_data.correct_answer === true || content_data.correct_answer === 'true';

        // Handle selection (doesn't reveal answer yet)
        const handleTFSelect = (answer: boolean) => {
          if (quizAnswered) return; // Can't change after checking
          setSelectedAnswer(answer ? 'true' : 'false');
          setResponse({ selected_answer: answer });
        };

        // Check/reveal answer
        const handleCheckTFAnswer = () => {
          if (!selectedAnswer) return;
          setQuizAnswered(true);
        };

        return (
          <div className="true-false-question enhanced">
            {/* Compact header row */}
            <div className="quiz-header-row">
              <span className="quiz-type-badge tf">✓✗ True/False</span>
              {content_data.difficulty_level && (
                <span className={`quiz-difficulty-badge ${content_data.difficulty_level}`}>
                  {content_data.difficulty_level === 'easy' && '🟢 Easy'}
                  {content_data.difficulty_level === 'medium' && '🟡 Medium'}
                  {content_data.difficulty_level === 'hard' && '🔴 Hard'}
                </span>
              )}
            </div>

            {/* Statement directly - no redundant title */}
            <div className="statement"><MarkdownRenderer inline>{content_data.statement || content_data.question_text}</MarkdownRenderer></div>

            <div className="tf-options">
              <label className={`tf-option ${selectedAnswer === 'true' && !quizAnswered ? 'selected' : ''} ${quizAnswered && selectedAnswer === 'true' ? (correctAnswer ? 'correct' : 'incorrect') : ''}`}>
                <input
                  type="radio"
                  name="tf_answer"
                  value="true"
                  onChange={() => handleTFSelect(true)}
                  checked={selectedAnswer === 'true'}
                  disabled={quizAnswered}
                />
                <span>✓ True</span>
              </label>
              <label className={`tf-option ${selectedAnswer === 'false' && !quizAnswered ? 'selected' : ''} ${quizAnswered && selectedAnswer === 'false' ? (!correctAnswer ? 'correct' : 'incorrect') : ''}`}>
                <input
                  type="radio"
                  name="tf_answer"
                  value="false"
                  onChange={() => handleTFSelect(false)}
                  checked={selectedAnswer === 'false'}
                  disabled={quizAnswered}
                />
                <span>✗ False</span>
              </label>
            </div>

            {/* Result card - unified feedback + AI triggers */}
            {quizAnswered && (() => {
              const isCorrect = (selectedAnswer === 'true') === correctAnswer;
              const tfExplanation = content_data.explanation;
              return (
                <div className="quiz-result-card">
                  <div className="result-header">
                    <span className={`result-badge ${isCorrect ? 'correct' : 'incorrect'}`}>
                      {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  </div>
                  {!isCorrect && (
                    <div className="correct-answer-info">
                      <strong>Correct Answer:</strong> {correctAnswer ? 'True' : 'False'}
                    </div>
                  )}
                  {tfExplanation && (
                    <button
                      className="explanation-toggle"
                      onClick={() => setShowQuizExplanation(!showQuizExplanation)}
                    >
                      {showQuizExplanation ? '▼ Hide Explanation' : '▶ Show Explanation'}
                    </button>
                  )}
                  {tfExplanation && showQuizExplanation && (
                    <div className="result-explanation"><MarkdownRenderer>{tfExplanation}</MarkdownRenderer></div>
                  )}
                  <ComponentTriggers
                    componentType="true_false"
                    triggerLocation="result_card"
                    subject={subject}
                    context={{ is_correct: isCorrect }}
                    sourceComponentType={current_component.component_type}
                    sourceComponentContent={current_component.content_data}
                    onTriggerClick={(prompt, sourceComponent) => {
                      setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                      setShowAIHelp(true);
                    }}
                    className="result-triggers"
                    fallbackTriggers={[
                      { label: '✨ Similar Question', prompt: 'Generate a similar practice question' }
                    ]}
                  />
                </div>
              );
            })()}

            {/* Two-step flow: Check Answer, then Continue (or options if optional) */}
            {!quizAnswered ? (
              <button
                onClick={handleCheckTFAnswer}
                className="check-answer-btn"
                disabled={!selectedAnswer || loading}
              >
                Check Answer
              </button>
            ) : isOptionalComponent() ? (
              <div className="quiz-actions">
                <button onClick={submitResponse} className="continue-btn primary" disabled={loading}>
                  Continue →
                </button>
                <button
                  onClick={async () => {
                    try {
                      await authFetch(`/api/micro-learning/modules/${selectedModule}/complete?student_id=${studentId}`, {
                        method: 'POST'
                      });
                    } catch (e) {
                      console.log('Could not mark complete:', e);
                    }
                    setCurrentSession(null);
                    setSelectedModule(null);
                  }}
                  className="finish-btn secondary"
                >
                  ✓ Done
                </button>
              </div>
            ) : (
              <button onClick={submitResponse} className="continue-btn" disabled={loading}>
                Continue
              </button>
            )}
          </div>
        );
      }

      case 'mnemonic':
        return (
          <div className="mnemonic">
            <div className="component-badge memory">🧠 Memory Aid</div>
            {content_data.mnemonic_type && (
              <span className="mnemonic-type-badge">{content_data.mnemonic_type}</span>
            )}
            <h2>{current_component.title}</h2>

            <div className="mnemonic-content">
              {content_data.concept && (
                <div className="mnemonic-concept">
                  <strong>What this helps you remember:</strong>
                  <p><MarkdownRenderer inline>{content_data.concept}</MarkdownRenderer></p>
                </div>
              )}

              <div className="mnemonic-phrase">
                <span className="mnemonic-icon">💡</span>
                <strong>{content_data.mnemonic_text || content_data.mnemonic || content_data.phrase}</strong>
              </div>

              {content_data.explanation && (
                <div className="mnemonic-explanation">
                  <strong>How it works:</strong>
                  <MarkdownRenderer>{content_data.explanation}</MarkdownRenderer>
                </div>
              )}

              {content_data.breakdown && (
                <div className="mnemonic-breakdown">
                  <h4>What it means:</h4>
                  <ul>
                    {content_data.breakdown.map((item: any, index: number) => (
                      <li key={index}>
                        <strong>{item.letter || item.key}:</strong> <MarkdownRenderer inline>{item.meaning || item.value}</MarkdownRenderer>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {content_data.when_to_use && (
                <div className="when-to-use">
                  <strong>When to use:</strong> <MarkdownRenderer inline>{content_data.when_to_use}</MarkdownRenderer>
                </div>
              )}

              {content_data.usage_tip && (
                <div className="usage-tip">
                  <strong>💡 How to use:</strong> <MarkdownRenderer inline>{content_data.usage_tip}</MarkdownRenderer>
                </div>
              )}

              {content_data.ethiopia_context && (
                <div className="ethiopia-context">
                  <EthiopianFlag size={16} style={{ marginRight: 6 }} />
                  <strong>Local context:</strong> {content_data.ethiopia_context}
                </div>
              )}

              {!content_data.mnemonic_text && !content_data.mnemonic && !content_data.phrase && (
                <div className="content-markdown">
                  <MarkdownRenderer>{content_data.content || content_data.content_markdown || ''}</MarkdownRenderer>
                </div>
              )}
            </div>

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Continue
            </button>
          </div>
        );

      case 'self_check': {
        const handleSelfCheck = (understood: boolean) => {
          setResponse({ understood });
          setQuizAnswered(true);
          track('microlearning.self_check.clicked', {
            module_id: selectedModule,
            component_id: current_component.component_id,
            understood,
          });
        };

        return (
          <div className="self-check">
            <div className="component-badge check">🤔 Self Check</div>
            <h2>{current_component.title}</h2>

            <div className="self-check-content">
              <div className="check-question">
                <MarkdownRenderer>{content_data.question || content_data.prompt || content_data.content || ''}</MarkdownRenderer>
              </div>

              {/* Expected knowledge - what the student should know */}
              {content_data.expected_knowledge && (
                <div className="expected-knowledge">
                  <strong>📚 You should be able to:</strong>
                  <p><MarkdownRenderer inline>{content_data.expected_knowledge}</MarkdownRenderer></p>
                </div>
              )}

              {/* Legacy hint support */}
              {content_data.hint && (
                <div className="check-hint">
                  <strong>💡 Hint:</strong> <MarkdownRenderer inline>{content_data.hint}</MarkdownRenderer>
                </div>
              )}
            </div>

            <div className="self-check-options">
              <div className="check-buttons">
                <button
                  className={`check-btn yes ${response.understood === true ? 'selected' : ''}`}
                  onClick={() => handleSelfCheck(true)}
                  disabled={quizAnswered}
                >
                  ✅ Yes, I understand
                </button>
                <button
                  className={`check-btn no ${response.understood === false ? 'selected' : ''}`}
                  onClick={() => handleSelfCheck(false)}
                  disabled={quizAnswered}
                >
                  🤔 Need more help
                </button>
              </div>
            </div>

            {quizAnswered && response.understood === false && (
              <div className="need-help-message">
                {content_data.if_unsure ? (
                  <p><strong>💡 Tip:</strong> <MarkdownRenderer inline>{content_data.if_unsure}</MarkdownRenderer></p>
                ) : (
                  <p>No worries! Use the AI Tutor below to get more explanation.</p>
                )}
              </div>
            )}

            <button
              onClick={submitResponse}
              className="continue-btn"
              disabled={loading}
            >
              Continue
            </button>
          </div>
        );
      }

      // NEW PARADIGM: ESLCE Intelligence Component
      case 'eslce_intel':
        return (
          <div className="eslce-intel">
            <div className="component-badge intel">🎯 ESLCE Intelligence</div>
            {/* Only show h2 if title adds info beyond the tab name */}
            {current_component.title && current_component.title !== 'ESLCE Exam Intelligence' && (
              <h2>{current_component.title}</h2>
            )}

            {/* Quick Tips Section */}
            {content_data.quick_tips && content_data.quick_tips.length > 0 && (
              <div className="intel-section tips-section">
                <h3>💡 Quick Tips for This Topic</h3>
                <ul className="quick-tips-list">
                  {content_data.quick_tips.map((tip: string, index: number) => (
                    <li key={index} className="quick-tip-item"><MarkdownRenderer inline>{tip}</MarkdownRenderer></li>
                  ))}
                </ul>
              </div>
            )}

            {/* Exam Frequency Stats */}
            {content_data.exam_frequency && (
              <div className="intel-section frequency-section">
                <h3>📊 How Often This Appears</h3>
                <div className="frequency-stats">
                  {content_data.exam_frequency.questions_per_exam && (
                    <div className="stat-item">
                      <span className="stat-value">{content_data.exam_frequency.questions_per_exam}</span>
                      <span className="stat-label">questions per exam</span>
                    </div>
                  )}
                  {content_data.exam_frequency.years_appeared && (
                    <div className="stat-item">
                      <span className="stat-value">{content_data.exam_frequency.years_appeared.length}</span>
                      <span className="stat-label">recent years</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Common Traps Section */}
            {content_data.traps && content_data.traps.length > 0 && (
              <div className="intel-section traps-section">
                <h3>⚠️ Watch Out For These Traps</h3>
                <div className="traps-list">
                  {content_data.traps.map((trap: any, index: number) => (
                    <div key={index} className="trap-card">
                      <div className="trap-header">
                        <span className="trap-icon">🚨</span>
                        <strong><MarkdownRenderer inline>{trap.trap}</MarkdownRenderer></strong>
                      </div>
                      {trap.why_students_fall && (
                        <div className="trap-why">
                          <em>Why students fall for it:</em> <MarkdownRenderer inline>{trap.why_students_fall}</MarkdownRenderer>
                        </div>
                      )}
                      {trap.correct_thinking && (
                        <div className="trap-correct">
                          <span className="correct-icon">✅</span>
                          <strong>Think instead:</strong> <MarkdownRenderer inline>{trap.correct_thinking}</MarkdownRenderer>
                        </div>
                      )}
                      {trap.tip && (
                        <div className="trap-tip">
                          💡 <strong>Pro tip:</strong> <MarkdownRenderer inline>{trap.tip}</MarkdownRenderer>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI triggers */}
            <ComponentTriggers
              componentType="eslce_intel"
              triggerLocation="component_inline"
              subject={subject}
              sourceComponentType="eslce_intel"
              sourceComponentContent={content_data}
              onTriggerClick={(prompt, sourceComponent) => {
                setAiHelpInitialRequest({ question: prompt, source_component: sourceComponent });
                setShowAIHelp(true);
              }}
              className="intel-triggers"
              size="medium"
              fallbackTriggers={[
                { label: '🎯 More Strategies', prompt: 'Give me more exam strategies for this topic' },
                { label: '📝 Practice Trap', prompt: 'Create a question that uses one of these traps' }
              ]}
            />

            <button onClick={submitResponse} className="continue-btn" disabled={loading}>
              Got it! Continue
            </button>
          </div>
        );

      // NEW PARADIGM: Assessment Set (5-question balanced quiz)
      // Flow: Answer ALL questions first, then submit to see ALL results at once
      case 'assessment_set': {
        const questions = content_data.questions || [];
        const quizInfo = getQuizNumbering();
        const isReviewMode = currentSession?.review_mode;

        // In review mode, check for previous answers from student_response
        const previousAnswers = currentSession?.component_list?.find(
          c => c.component_id === current_component.component_id
        )?.student_response?.response_data || {};

        // Use previous answers if in review mode and we haven't started answering yet (and not retaking)
        const effectiveAnswers = Object.keys(assessmentAnswers).length === 0 && isReviewMode && Object.keys(previousAnswers).length > 0 && !isRetakingQuiz
          ? previousAnswers
          : assessmentAnswers;

        const allAnswered = Object.keys(effectiveAnswers).length === questions.length;
        // In review mode, show as submitted if we have previous answers
        // Quiz is submitted if answers have been revealed, OR if in review mode with previous answers
        // BUT if user clicked Retake (isRetakingQuiz=true), allow retaking even in review mode
        const isSubmitted = assessmentRevealed.size > 0 || (isReviewMode && Object.keys(previousAnswers).length > 0 && !isRetakingQuiz);

        // Calculate score using effective answers
        const correctCount = Object.entries(effectiveAnswers).filter(
          ([idx, ans]) => questions[parseInt(idx)]?.correct_answer === ans
        ).length;

        const handleAssessmentSelect = (qIndex: number, opt: string) => {
          // Don't allow changes if quiz is submitted (unless retaking)
          if (isSubmitted) return;
          setAssessmentAnswers(prev => ({ ...prev, [qIndex]: opt }));
        };

        const handleSubmitAssessment = async () => {
          if (!allAnswered) return;

          track('microlearning.assessment.submitted', {
            module_id: selectedModule,
            component_id: current_component.component_id,
            total: questions.length,
            correct: correctCount,
            time_spent_s: timeSpent,
          });

          // Reveal all at once
          const allIndices = new Set<number>(questions.map((_: any, idx: number) => idx));
          setAssessmentRevealed(allIndices);
          setIsRetakingQuiz(false);  // Clear retake flag when submitting

          // Save the assessment response to the backend
          try {
            const submitResponse = await authFetch(
              `/api/micro-learning/modules/${selectedModule}/components/${current_component.component_id}/response?student_id=${studentId}`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  response_data: effectiveAnswers,
                  time_spent_seconds: timeSpent
                })
              }
            );

            if (submitResponse.ok) {
              const result = await submitResponse.json();
              console.log('Assessment saved:', result);

              // Update session statistics
              setQuestionsAttempted(prev => prev + questions.length);
              setQuestionsCorrect(prev => prev + correctCount);
            }
          } catch (err) {
            console.error('Failed to save assessment:', err);
          }
        };

        const handleRetakeQuiz = () => {
          track('microlearning.retake_quiz.clicked', {
            module_id: selectedModule,
            component_id: current_component.component_id,
          });
          // Clear answers and revealed state to retake
          setAssessmentAnswers({});
          setAssessmentRevealed(new Set());
          setIsRetakingQuiz(true);
        };

        // Normalize options helper
        const normalizeOptions = (opts: any) => {
          if (Array.isArray(opts)) return opts;
          if (opts && typeof opts === 'object') {
            return Object.entries(opts).map(([key, value]) => ({ label: key, text: value }));
          }
          return [];
        };

        return (
          <div className={`assessment-set ${isSubmitted ? 'submitted' : ''} ${isReviewMode ? 'review-mode' : ''}`}>
            <div className="component-badge assessment">
              📝 Quiz {quizInfo.total > 1 ? `${quizInfo.current} of ${quizInfo.total}` : ''}
            </div>

            {/* Score summary at top when submitted */}
            {isSubmitted && (
              <div className="score-summary-top">
                <span className="score-value">{correctCount}/{questions.length}</span>
                <span className="score-message">
                  {correctCount === questions.length ? '🎉 Perfect!' :
                   correctCount >= questions.length * 0.8 ? '👏 Great job!' :
                   correctCount >= questions.length * 0.6 ? '👍 Good effort!' :
                   'Keep practicing!'}
                </span>
              </div>
            )}

            {/* Progress indicator - shows score when submitted, answered count otherwise */}
            <div className="assessment-progress">
              <span className="question-counter">
                {isSubmitted
                  ? `${correctCount}/${questions.length} correct`
                  : `${Object.keys(effectiveAnswers).length}/${questions.length} answered`
                }
              </span>
              <div className="progress-dots">
                {questions.map((_: any, idx: number) => {
                  const hasAnswer = effectiveAnswers[idx] !== undefined;
                  const isCorrect = hasAnswer && effectiveAnswers[idx] === questions[idx].correct_answer;

                  return (
                    <span
                      key={idx}
                      className={`dot ${
                        isSubmitted
                          ? (isCorrect ? 'correct' : (hasAnswer ? 'incorrect' : 'unanswered'))
                          : (hasAnswer ? 'answered' : '')
                      }`}
                      title={isSubmitted
                        ? (isCorrect ? `Q${idx+1}: Correct` : (hasAnswer ? `Q${idx+1}: Incorrect` : `Q${idx+1}: Not answered`))
                        : (hasAnswer ? `Q${idx+1}: Answered` : `Q${idx+1}: Not answered`)
                      }
                    />
                  );
                })}
              </div>
              {/* Retake button in review mode */}
              {isReviewMode && isSubmitted && (
                <button className="retake-quiz-btn" onClick={handleRetakeQuiz}>
                  ↺ Retake Quiz
                </button>
              )}
            </div>

            {/* All questions displayed */}
            <div className="assessment-questions">
              {questions.map((q: any, qIndex: number) => {
                const opts = normalizeOptions(q.options);
                const selectedOpt = effectiveAnswers[qIndex];
                const isCorrect = selectedOpt === q.correct_answer;

                // A quiz's five questions often share one passage — our
                // importer copies it onto each item so no surface can lose it.
                // Printing a 900-character passage five times down one screen
                // is its own kind of unreadable, so it is drawn once, above
                // the first question that uses it.
                const prevPassage = qIndex > 0 ? questions[qIndex - 1]?.passage : null;
                const showPassage = q.passage && q.passage !== prevPassage;

                return (
                  <div key={qIndex} className="assessment-question">
                    {showPassage && (
                      <div className="question-stimulus">
                        <MarkdownRenderer className="stimulus-body">
                          {q.passage}
                        </MarkdownRenderer>
                      </div>
                    )}
                    <div className="question-text">
                      <span className="q-num">{qIndex + 1}.</span> <MarkdownRenderer inline>{q.question_text || ''}</MarkdownRenderer>
                    </div>

                    {/* The stimulus, on items whose question IS the figure —
                        a chart or table the stem deliberately does not restate.
                        Without this the question is unanswerable, not merely
                        plainer. Absent on most items. */}
                    <ContentFigure figure={q.figure} />

                    <div className="options compact">
                      {opts.map((option: any) => {
                        const optLabel = option.label || option.key;
                        const optText = option.text || option.value;
                        const isSelected = selectedOpt === optLabel;
                        const isCorrectOpt = optLabel === q.correct_answer;

                        // Enhanced class logic for better visual distinction
                        let optClass = "option";
                        if (isSubmitted) {
                          if (isSelected && isCorrectOpt) {
                            optClass += " selected-correct";
                          } else if (isSelected && !isCorrectOpt) {
                            optClass += " selected-incorrect";
                          } else if (!isSelected && isCorrectOpt) {
                            optClass += " unselected-correct";
                          } else {
                            optClass += " unselected-neutral";
                          }
                        } else if (isSelected) {
                          optClass += " selected";
                        }

                        return (
                          <label key={optLabel} className={optClass}>
                            <input
                              type="radio"
                              name={`assessment_q${qIndex}`}
                              value={optLabel}
                              onChange={() => handleAssessmentSelect(qIndex, optLabel)}
                              checked={isSelected}
                              disabled={isSubmitted}
                            />
                            <span className="option-text">{optLabel}. <MarkdownRenderer inline>{optText || ''}</MarkdownRenderer></span>
                            {/* Badges shown after submission */}
                            {isSubmitted && (
                              <span className="option-badges">
                                {isSelected && (
                                  <span className={`badge your-answer-badge ${isCorrectOpt ? 'correct' : 'incorrect'}`}>
                                    {isCorrectOpt ? '✓ Your answer' : '✗ Your answer'}
                                  </span>
                                )}
                                {isCorrectOpt && !isSelected && (
                                  <span className="badge correct-badge">✓ Correct</span>
                                )}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {/* Explanation toggle after submit */}
                    {isSubmitted && q.explanation && (
                      <div className="explanation-section">
                        <button
                          className="explanation-toggle"
                          onClick={() => {
                            const newSet = new Set(assessmentExplanationsExpanded);
                            if (newSet.has(qIndex)) {
                              newSet.delete(qIndex);
                            } else {
                              newSet.add(qIndex);
                            }
                            setAssessmentExplanationsExpanded(newSet);
                          }}
                        >
                          {assessmentExplanationsExpanded.has(qIndex) ? '▼ Hide explanation' : '▶ Show explanation'}
                        </button>
                        {assessmentExplanationsExpanded.has(qIndex) && (
                          <div className="result-explanation"><MarkdownRenderer>{q.explanation}</MarkdownRenderer></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Submit / Next Quiz button */}
            {!isSubmitted ? (
              <button
                onClick={handleSubmitAssessment}
                className="submit-assessment-btn"
                disabled={!allAnswered}
              >
                {allAnswered ? 'Submit Quiz' : `Answer all ${questions.length} questions`}
              </button>
            ) : (
              <div className="assessment-complete">
                <div className="quiz-actions">
                  <button
                    onClick={async () => {
                      track('microlearning.assessment.continue_clicked', {
                        module_id: selectedModule,
                        component_id: current_component.component_id,
                      });
                      // Skip past remaining assessments to next content
                      try {
                        setLoading(true);
                        const skipResponse = await authFetch(
                          `/api/micro-learning/modules/${selectedModule}/skip-past-assessments?student_id=${studentId}`,
                          { method: 'POST' }
                        );
                        const result = await skipResponse.json();

                        // Helper to handle module completion
                        const handleModuleCompletion = async () => {
                          try {
                            await authFetch(`/api/micro-learning/modules/${selectedModule}/complete?student_id=${studentId}`, {
                              method: 'POST'
                            });
                          } catch (e) {
                            console.log('Could not mark complete:', e);
                          }
                          if (onModuleComplete) {
                            onModuleComplete({});
                            return;
                          }
                          setCurrentSession(null);
                          setSelectedModule(null);
                        };

                        // Check if skip was successful
                        if (!skipResponse.ok || !result.success) {
                          console.log('Skip failed, marking module complete');
                          await handleModuleCompletion();
                          return;
                        }

                        if (result.module_complete) {
                          // No more content, complete the module
                          await handleModuleCompletion();
                        } else if (result.new_component_index !== undefined && selectedModule) {
                          // Successfully skipped to new content, reload session
                          startModule(selectedModule);
                        } else {
                          // Unexpected response, complete the module
                          await handleModuleCompletion();
                        }
                      } catch (e) {
                        console.log('Error skipping:', e);
                        // On error, complete the module
                        try {
                          await authFetch(`/api/micro-learning/modules/${selectedModule}/complete?student_id=${studentId}`, {
                            method: 'POST'
                          });
                        } catch (completeErr) {
                          console.log('Could not mark complete:', completeErr);
                        }
                        if (onModuleComplete) {
                          onModuleComplete({});
                          return;
                        }
                        setCurrentSession(null);
                        setSelectedModule(null);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="continue-btn primary"
                    disabled={loading}
                  >
                    Continue →
                  </button>
                  {hasMoreAssessments() ? (
                    <button
                      onClick={() => {
                        track('microlearning.more_quizzes.clicked', {
                          module_id: selectedModule,
                          component_id: current_component.component_id,
                        });
                        goToNextAssessment();
                      }}
                      className="more-quiz-btn secondary"
                      disabled={loading}
                    >
                      📝 More Quizzes
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        track('microlearning.module.done_clicked', {
                          module_id: selectedModule,
                        });
                        // Mark module as complete and go back
                        try {
                          await authFetch(`/api/micro-learning/modules/${selectedModule}/complete?student_id=${studentId}`, {
                            method: 'POST'
                          });
                        } catch (e) {
                          console.log('Could not mark complete:', e);
                        }
                        if (onModuleComplete) {
                          onModuleComplete({});
                          return;
                        }
                        setCurrentSession(null);
                        setSelectedModule(null);
                      }}
                      className="finish-btn secondary"
                    >
                      ✓ Done
                    </button>
                  )}
                </div>

                {/* Quiz Score Card with performance tier (like Telegram bot) */}
                <div className="quiz-score-card">
                  {quizInfo.total > 1 && (
                    <div className="quiz-card-header">Quiz {quizInfo.current} of {quizInfo.total}</div>
                  )}
                  <div className="quiz-card-score">
                    <span className="tier-emoji">{getPerformanceTier(Math.round((correctCount / questions.length) * 100)).emoji}</span>
                    <span className="score-fraction">{correctCount}/{questions.length}</span>
                    <span className="score-percent">({Math.round((correctCount / questions.length) * 100)}%)</span>
                    <span className="tier-message">{getPerformanceTier(Math.round((correctCount / questions.length) * 100)).message}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      default:
        return <div className="unknown-component">Unknown component type: {current_component.component_type}</div>;
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>⚠️ Learning Mode Issue</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button
              onClick={() => {
                setError(null);
                setCurrentSession(null);
                setSelectedModule(null);
              }}
              className="back-to-modules-btn"
            >
              ← Back to Modules
            </button>
          </div>
        </div>
      </div>
    );
  }

  const restartModule = () => {
    // Use either selectedModule or moduleId prop
    const modId = selectedModule || moduleId;
    if (modId) {
      setCurrentSession(null);
      setResponse({});
      setFlashcardFlipped(false);
      setQuizAnswered(false);
      setSelectedAnswer(null);
      setShowQuizExplanation(false);
      setAnswerHistory([]);
      setFirstSelectionTime(null);
      setTimeSpent(0);
      setShowAIHelp(false);
      // Restart the module from the beginning
      setTimeout(() => startModule(modId, true), 150);
    }
  };

  // Jump to a specific component by index (for review mode navigation)
  const jumpToComponent = async (componentIndex: number) => {
    const modId = selectedModule || moduleId;
    if (!modId) return;

    try {
      setLoading(true);
      // Preserve review mode when jumping between components
      const isInReview = currentSession?.review_mode;
      const response = await authFetch(
        `/api/micro-learning/modules/${modId}/jump-to-component?student_id=${studentId}&component_index=${componentIndex}${isInReview ? '&review=true' : ''}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        // Reset component state
        setFlashcardFlipped(false);
        setQuizAnswered(false);
        setSelectedAnswer(null);
        setShowQuizExplanation(false);
        setAnswerHistory([]);
        setFirstSelectionTime(null);
        setExpandedDefinitions(new Set());
        setAssessmentIndex(0);
        setAssessmentAnswers({});
        setAssessmentRevealed(new Set());
        setAssessmentExplanationsExpanded(new Set());

        // Update session with new component
        setCurrentSession(data);
      } else {
        console.error('Failed to jump to component');
      }
    } catch (err) {
      console.error('Error jumping to component:', err);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to next assessment_set (for "More Quizzes" button)
  const goToNextAssessment = async () => {
    if (!currentSession?.component_list) return;
    const currentIndex = currentSession.progress.current_component_index;

    // Find the next assessment_set after current position
    const nextAssessmentIndex = currentSession.component_list.findIndex((c, idx) =>
      idx > currentIndex && c.component_type === 'assessment_set'
    );

    if (nextAssessmentIndex !== -1) {
      // Jump to the next assessment
      await jumpToComponent(nextAssessmentIndex);
    }
  };

  if (currentSession) {
    // If onExit is provided, we're embedded in MicroLearning page which has its own header
    const isEmbedded = !!onExit;

    const componentType = currentSession.current_component.component_type;

    return (
      <div className={`micro-learning-session ${isEmbedded ? 'embedded' : ''} component-type-${componentType}`}>
        {/* Session context bar — subject · module · progress */}
        <div className="session-context-bar">
          <div className="context-left">
            <span className="context-subject">{
              subject ? (({ Mathematics: 'Math', Biology: 'Bio', Chemistry: 'Chem', Geography: 'Geo', Economics: 'Econ', Aptitude: 'Apt' } as Record<string, string>)[subject] || subject) : ''
            }</span>
            <span className="context-sep">›</span>
            <span className="context-module">{currentSession.module.title}</span>
          </div>
          <div className="context-right">
            <span className="context-step">
              {currentSession.progress.current_component_index + 1}/{currentSession.progress.total_components}
            </span>
            {currentSession.progress.score > 0 && (
              <span className="context-score">🏆 {currentSession.progress.score}</span>
            )}
            <button
              onClick={restartModule}
              className="context-overflow"
              title="Restart module"
            >
              ↺
            </button>
          </div>
        </div>

        {/* Section Navigation Bar - always visible */}
        {currentSession.component_list && (
          <div className="section-nav-bar">
            {(() => {
              // A tab renders even when the module has no component of that
              // type — greyed out, but present. For ESSLCE that is honest:
              // the module could have exam intel or a past-exam question and
              // this one does not. A UAT lesson can have NEITHER, ever: there
              // is no ESSLCE intelligence for the AAU entrance test and no
              // past paper to draw from, so both tabs are permanently dead
              // furniture there and are dropped instead.
              const ALL_SECTIONS = [
                { type: 'eslce_intel' as const, label: 'Exam Intel', icon: '🎯' },
                { type: 'key_definitions' as const, label: 'Key Concepts', icon: '📖' },
                { type: 'practice_mcq' as const, label: 'Practice', icon: '✏️' },
                { type: 'assessment_set' as const, label: 'Quiz', icon: '📋' },
                { type: 'real_eslce_question' as const, label: 'Past Exam', icon: '📜' }
              ];
              // Was `subject === 'UAT'`. The reasoning in the comment above is
              // right and the test was not: it names one package, so every
              // package added afterwards inherits two tabs it can never fill.
              // EAU shipped showing "Exam Intel" and "Past Exam" — five
              // sections against UAT's three — for an entrance exam with no
              // ESSLCE intelligence and no past ESLCE paper behind it.
              //
              // Asked of the CONTENT instead: a module that has no component
              // of a type, in a subject whose modules never carry one, does
              // not advertise it. ESSLCE modules are unaffected — theirs do
              // carry these types, so the tabs still render, greyed when a
              // particular module lacks one.
              const listTypes = new Set(
                (currentSession.component_list || []).map(c => c.component_type)
              );
              const everCarries = (t: string) =>
                listTypes.has(t) || ESLCE_ONLY_SUBJECTS.has(subject || '');
              const sections = ALL_SECTIONS.filter(
                s =>
                  (s.type !== 'eslce_intel' && s.type !== 'real_eslce_question') ||
                  everCarries(s.type)
              );

              const currentType = currentSession.component_list![currentSession.progress.current_component_index]?.component_type;
              const sectionCompletion = currentSession.section_completion;
              const componentList = currentSession.component_list!;
              const respondedIds = new Set(
                componentList.filter(c => c.student_response).map(c => c.component_id)
              );

              // Count components by type
              const practiceComponents = componentList.filter(c => c.component_type === 'practice_mcq');
              const practiceAnswered = practiceComponents.filter(c => respondedIds.has(c.component_id)).length;
              const practiceTotal = practiceComponents.length;

              const quizComponents = componentList.filter(c => c.component_type === 'assessment_set');
              const quizzesAnswered = quizComponents.filter(c => respondedIds.has(c.component_id)).length;
              const quizzesTotal = quizComponents.length;

              const eslceComponents = componentList.filter(c => c.component_type === 'real_eslce_question');
              const eslceAnswered = eslceComponents.filter(c => respondedIds.has(c.component_id)).length;
              const eslceTotal = eslceComponents.length;

              // Helper to get count badge for a section type
              const getCountBadge = (sectionType: string) => {
                switch (sectionType) {
                  case 'practice_mcq':
                    return practiceTotal > 1 ? `${practiceAnswered}/${practiceTotal}` : null;
                  case 'assessment_set':
                    return quizzesTotal > 1 ? `${quizzesAnswered}/${quizzesTotal}` : null;
                  case 'real_eslce_question':
                    return eslceTotal > 1 ? `${eslceAnswered}/${eslceTotal}` : null;
                  default:
                    return null;
                }
              };

              return (
                <>
                  {sections.map(section => {
                    const compIndex = componentList.findIndex(
                      c => c.component_type === section.type
                    );
                    const exists = compIndex !== -1;
                    const isActive = currentType === section.type;
                    const isCompleted = sectionCompletion?.[section.type] || false;
                    const countBadge = getCountBadge(section.type);

                    return (
                      <button
                        key={section.type}
                        className={`section-nav-item ${isActive ? 'active' : ''} ${!exists ? 'disabled' : ''} ${isCompleted && !isActive ? 'completed' : ''}`}
                        onClick={() => exists && jumpToComponent(compIndex)}
                        disabled={loading || !exists}
                        title={exists ? `Go to ${section.label}${isCompleted ? ' (Done)' : ''}` : `${section.label} not available`}
                      >
                        <span className="section-tab-text">
                          <span className="tab-icon">{section.icon}</span>
                          {section.label}
                          {isCompleted && <span className="tab-check">✓</span>}
                          {countBadge && <span className="tab-count">{countBadge}</span>}
                        </span>
                      </button>
                    );
                  })}
                  {/* Quick "More Quizzes" button - always visible when more quizzes exist */}
                  {hasMoreAssessments() && (
                    <button
                      className="section-nav-item more-quizzes-quick"
                      onClick={goToNextAssessment}
                      disabled={loading}
                      title="Take the next quiz"
                    >
                      <span className="section-nav-icon">➡️</span>
                      <span className="section-nav-label">Next Quiz</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Review Mode Summary Banner - student-friendly display */}
        {currentSession.review_mode && currentSession.component_list && (
          <div className="review-mode-banner">
            <span className="review-badge">Review</span>

            {/* Calculate stats from component list */}
            {(() => {
              const components = currentSession.component_list!;
              const respondedIds = new Set(components.filter(c => c.student_response).map(c => c.component_id));

              const practiceComps = components.filter(c => c.component_type === 'practice_mcq');
              const practiceAnswered = practiceComps.filter(c => respondedIds.has(c.component_id)).length;

              const quizComps = components.filter(c => c.component_type === 'assessment_set');
              const quizzesAnswered = quizComps.filter(c => respondedIds.has(c.component_id)).length;

              const eslceComps = components.filter(c => c.component_type === 'real_eslce_question');
              const eslceAnswered = eslceComps.filter(c => respondedIds.has(c.component_id)).length;

              const timeSpent = currentSession.review_stats?.time_spent_seconds ?? 0;

              return (
                <>
                  {practiceComps.length > 0 && (
                    <span className="review-stat" title="Practice questions answered">
                      ✏️ {practiceAnswered}/{practiceComps.length}
                    </span>
                  )}
                  {quizComps.length > 0 && (
                    <span className="review-stat" title="Quizzes taken">
                      📋 {quizzesAnswered}/{quizComps.length}
                    </span>
                  )}
                  {eslceComps.length > 0 && (
                    <span className="review-stat" title="Past exam questions answered">
                      📜 {eslceAnswered}/{eslceComps.length}
                    </span>
                  )}
                  {timeSpent > 0 && (
                    <span className="review-stat time-stat">
                      ⏱ {timeSpent >= 60 ? `${Math.floor(timeSpent / 60)}m ${timeSpent % 60}s` : `${timeSpent}s`}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Quiz Scores Dashboard in Review Mode */}
        {currentSession.review_mode && currentSession.review_stats?.quiz_scores && currentSession.review_stats.quiz_scores.length > 0 && (
          <div className="quiz-scores-dashboard">
            <div className="dashboard-header">
              <span className="dashboard-title">Quiz Performance</span>
              <span className="dashboard-overall">
                Overall: {Math.round(currentSession.review_stats.quiz_accuracy || 0)}%
              </span>
            </div>
            <div className="dashboard-scores">
              {currentSession.review_stats.quiz_scores.map((quiz, idx) => {
                const tier = getPerformanceTier(quiz.percentage);
                return (
                  <div key={idx} className="quiz-score-item">
                    <span className="quiz-name">{quiz.quiz_title || `Quiz ${idx + 1}`}</span>
                    <span className="quiz-score-display">
                      <span className="tier-emoji">{tier.emoji}</span>
                      <span className="score-numbers">{quiz.correct}/{quiz.total}</span>
                      <span className="score-pct">({quiz.percentage}%)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Module title banner removed — context bar shows subject + module title */}

        {currentSession.previous_component_result && showPreviousResult && (
          <div className={`previous-result ${currentSession.previous_component_result.is_correct ? 'correct' : 'incorrect'} fade-out`}>
            {currentSession.previous_component_result.feedback}
          </div>
        )}

        {stuckPrompt && (() => {
          const parts: string[] = [];
          if (stuckPrompt.coreUnanswered > 0) {
            parts.push(
              `${stuckPrompt.coreUnanswered} practice question${stuckPrompt.coreUnanswered === 1 ? '' : 's'}`,
            );
          }
          if (stuckPrompt.quizNeeded) parts.push('a quiz to take');
          if (stuckPrompt.pastExamNeeded) parts.push('a past-exam to answer');
          // Natural sentence: "X, Y and Z"
          let summary = '';
          if (parts.length === 1) summary = parts[0];
          else if (parts.length === 2) summary = `${parts[0]} and ${parts[1]}`;
          else if (parts.length === 3)
            summary = `${parts[0]}, ${parts[1]}, and ${parts[2]}`;

          return (
            <div className="stuck-prompt-overlay" role="dialog" aria-modal="true">
              <div className="stuck-prompt-card">
                <div className="stuck-prompt-title">
                  🎯 You've reached the end
                </div>
                <div className="stuck-prompt-body">
                  To mark this module complete, you still have{' '}
                  <strong>{summary}</strong> left. No pressure — what would
                  you like to do?
                </div>
                <div className="stuck-prompt-actions">
                  <button
                    type="button"
                    className="stuck-prompt-btn primary"
                    onClick={async () => {
                      const idx = stuckPrompt.firstUnfinishedIndex;
                      setStuckPrompt(null);
                      await jumpToComponent(idx);
                    }}
                  >
                    ↩️ Finish the rest
                  </button>
                  <button
                    type="button"
                    className="stuck-prompt-btn secondary"
                    onClick={() => {
                      setStuckPrompt(null);
                      if (onExit) {
                        onExit();
                      } else {
                        setCurrentSession(null);
                        setSelectedModule(null);
                        loadModules();
                      }
                    }}
                  >
                    ➡️ Try another module
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="component-container">
          <Reportable
            entityType="module_component"
            entityId={currentSession.current_component.component_id}
            subTargets={['stem', 'options', 'explanation', 'image', 'topic']}
          >
            {renderComponent()}
          </Reportable>
        </div>

        {/* AI Help Button - outside the content box */}
        <AIHelpPanel
          source="web_lesson"
          context={
            currentSession.current_component.component_type === 'practice_mcq' ||
            currentSession.current_component.component_type === 'assessment_set' ||
            currentSession.current_component.component_type === 'real_esslce_question'
              ? 'quiz'
              : 'content'
          }
          componentType={currentSession.current_component.component_type}
          moduleId={selectedModule || undefined}
          componentId={currentSession.current_component.component_id}
          /* A quiz holds five questions in an array, so the component has no
             stem of its own — Ask Lomi opened inside one knew the module and
             the component and nothing about the question actually on screen.
             `assessmentIndex` is the question the student is looking at. */
          questionText={
            currentSession.current_component.component_type === 'assessment_set'
              ? (currentSession.current_component.content_data?.questions?.[assessmentIndex]
                  ?.question_text ||
                 currentSession.current_component.content_data?.questions?.[assessmentIndex]
                  ?.question)
              : (currentSession.current_component.content_data?.question_text ||
                 currentSession.current_component.content_data?.stem ||
                 currentSession.current_component.content_data?.question ||
                 currentSession.current_component.content_data?.statement)
          }
          /* Without an id the answer cannot be attached to the question a
             student asked about: no generate-similar, and the exchange is
             invisible to the practice-question tracking. */
          questionId={
            currentSession.current_component.component_type === 'assessment_set'
              ? currentSession.current_component.content_data?.questions?.[assessmentIndex]
                  ?.question_id
              : currentSession.current_component.content_data?.question_id
          }
          contentTitle={currentSession.current_component.title}
          isOpen={showAIHelp}
          onToggle={() => setShowAIHelp(!showAIHelp)}
          sessionState={{
            timeOnComponentSeconds: timeSpent,
            lastAnswer: selectedAnswer || undefined,
            lastAnswerCorrect: lastAnswerCorrect,
            consecutiveWrong: consecutiveWrong,
            questionsAttempted: questionsAttempted,
            questionsCorrect: questionsCorrect
          }}
          initialRequest={aiHelpInitialRequest}
          onInitialRequestSent={() => setAiHelpInitialRequest(undefined)}
        />

        {/* Compact footer with score */}
        <div className="session-footer-compact">
          <span className="score-display">
            🏆 {currentSession.progress.score}/{currentSession.progress.max_possible_score} pts
          </span>
          <span className="time-display">
            ⏱ {Math.floor(timeSpent / 60)}:{(timeSpent % 60).toString().padStart(2, '0')}
          </span>
        </div>

        {/* Toast notifications - positioned at top to avoid blocking buttons */}
        <ToastContainer toasts={toasts} onDismiss={dismissToast} position="top" />
      </div>
    );
  }

  // Helper to determine module status
  const getModuleStatus = (module: Module) => {
    if (!module.student_progress) return 'not_started';
    return module.student_progress.status;
  };

  // Get recommended next module (first not-completed module)
  const recommendedModule = modules.find(m =>
    getModuleStatus(m) !== 'completed'
  );

  // Separate completed and other modules
  const completedModules = modules.filter(m => getModuleStatus(m) === 'completed');
  const otherModules = modules.filter(m => getModuleStatus(m) !== 'completed');

  // Get button text based on status
  const getButtonText = (module: Module) => {
    if (loading) return 'Loading...';
    const status = getModuleStatus(module);
    if (status === 'completed') return '↻ Review Again';
    if (status === 'in_progress') return '▶ Continue';
    return '▶ Start Learning';
  };

  // Render a module card
  const renderModuleCard = (module: Module, isRecommended: boolean = false) => {
    const status = getModuleStatus(module);
    const isCompleted = status === 'completed';
    const isInProgress = status === 'in_progress';

    return (
      <div
        key={module.module_id}
        className={`module-card ${isCompleted ? 'completed' : ''} ${isInProgress ? 'in-progress' : ''} ${isRecommended ? 'recommended' : ''}`}
      >
        {/* Status Badge */}
        {isCompleted && (
          <div className="status-badge completed">
            <span className="badge-icon">✓</span>
            <span className="badge-text">Completed</span>
          </div>
        )}
        {isInProgress && (
          <div className="status-badge in-progress">
            <span className="badge-icon">◐</span>
            <span className="badge-text">In Progress</span>
          </div>
        )}

        <div className="module-header">
          <h3>{module.title}</h3>
          <span className={`difficulty ${module.difficulty_level}`}>
            {module.difficulty_level}
          </span>
        </div>

        <p className="module-description">{module.description}</p>

        <div className="module-meta">
          <div className="subject-topic">
            {module.subject} • {module.topic}
            {module.subtopic && ` • ${module.subtopic}`}
          </div>
          <div className="duration">
            ⏱️ {module.estimated_duration_minutes} min
          </div>
          <div className="components">
            📚 {module.total_components} components
            {module.total_questions > 0 && ` • ❓ ${module.total_questions} questions`}
            {module.total_flashcards > 0 && ` • 🗂️ ${module.total_flashcards} flashcards`}
          </div>
        </div>

        {/* Progress bar for in-progress or completed modules */}
        {module.student_progress && (
          <div className={`progress-summary ${isCompleted ? 'completed' : ''}`}>
            <div className="progress-bar">
              <div
                className={`progress-fill ${isCompleted ? 'completed' : ''}`}
                style={{ width: `${Math.min(100, module.student_progress.completion_percentage || 0)}%` }}
              />
            </div>
            <div className="progress-text">
              {isCompleted ? (
                <>
                  <span className="completion-check">✓</span>
                  {module.student_progress.score > 0 && (
                    <span className="score">Score: {module.student_progress.score}/{module.student_progress.max_score}</span>
                  )}
                </>
              ) : (
                <>
                  <span>{Math.round(module.student_progress.completion_percentage || 0)}% complete</span>
                  {module.student_progress.score > 0 && (
                    <span className="score"> • {module.student_progress.score} pts</span>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className="module-actions">
          <button
            onClick={() => startModule(module.module_id, false, isCompleted)}
            className={`start-module-btn ${isCompleted ? 'review' : ''} ${isInProgress ? 'continue' : ''}`}
            disabled={loading}
          >
            {getButtonText(module)}
          </button>
        </div>
      </div>
    );
  };

  // Available subjects for filtering
  const availableSubjects = ['Biology', 'Chemistry', 'Physics', 'Mathematics', 'English', 'Aptitude', 'Geography', 'History', 'Economics'];

  return (
    <div className="micro-learning-modules">
      <h1>Micro Learning Modules</h1>

      {/* Subject Filter */}
      <div className="module-filters">
        <span className="filter-label">Filter by subject:</span>
        <div className="filter-chips">
          <button
            className={`filter-chip ${!selectedSubjectFilter ? 'active' : ''}`}
            onClick={() => setSelectedSubjectFilter(null)}
          >
            All
          </button>
          {availableSubjects.map(subj => (
            <button
              key={subj}
              className={`filter-chip ${selectedSubjectFilter === subj ? 'active' : ''}`}
              onClick={() => setSelectedSubjectFilter(subj)}
            >
              {subj}
            </button>
          ))}
        </div>
      </div>

      {modules.length === 0 ? (
        <div className="no-modules">
          {selectedSubjectFilter
            ? `No ${selectedSubjectFilter} modules available yet.`
            : 'No modules available yet. Check back later!'}
        </div>
      ) : (
        <>
          {/* CoachCard hidden for launch — "Continue Where You Left Off" + "Recommended For You" below already handle this. */}
          {/* {subject && (
            <CoachCard
              subject={subject}
              location="micro_learning"
              onAction={(action: CoachAction) => {
                if (action.type === 'continue_module' && action.payload) {
                  startModule(action.payload);
                } else if (recommendedModule) {
                  startModule(recommendedModule.module_id);
                }
              }}
            />
          )} */}

          {/* Recommended Next Module */}
          {recommendedModule && (
            <div className="recommended-section">
              <h2 className="section-title">
                {getModuleStatus(recommendedModule) === 'in_progress'
                  ? '📚 Continue Where You Left Off'
                  : '🎯 Recommended For You'}
              </h2>
              <div className="recommended-module">
                {renderModuleCard(recommendedModule, true)}
              </div>
            </div>
          )}

          {/* Completed Modules Section */}
          {completedModules.length > 0 && (
            <div className="completed-section">
              <h2 className="section-title">
                ✅ Completed ({completedModules.length})
              </h2>
              <div className="modules-grid compact">
                {completedModules.map((module) => renderModuleCard(module))}
              </div>
            </div>
          )}

          {/* Other Modules */}
          {otherModules.length > 1 && (
            <div className="all-modules-section">
              <h2 className="section-title">
                📖 All Modules ({otherModules.length})
              </h2>
              <div className="modules-grid">
                {otherModules
                  .filter(m => m.module_id !== recommendedModule?.module_id)
                  .map((module) => renderModuleCard(module))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MicroLearningModule;
